import {assertRequestOperationSucceeded} from "shared/api/requestOperation";
import {prepareFileBlob} from "shared/utils/safeFileBlob";
import {BiziboxDataHealth} from "shared/types/BiziboxDataHealth";
import {Message} from "shared/types/Message";
import {Company} from "shared/types/Company";
import {CompanyDetails} from "shared/types/CompanyDetails";
import {User} from "shared/types/User";
import {CompanyStatus} from "shared/types/CompanyStatuses";
import {jwtDecode} from "jwt-decode";
import {CompanyEvent, GetCompanyDetailsResponse, GetFinanceDataResponse} from "shared/types/CompanyEvent";
import {MeetingActionItem, MeetingTaskAssigneeOption} from "../types/MeetingActionItem";
import {CalendarEventDto} from "shared/types/calendar";
import {Meeting, MeetingParticipant} from "../types/Meeting";
import {
    normalizeUserAccountRole,
    shouldUseUserAccountsExperience,
    decodeAuthTokenClaims,
    isUserAccountsSession,
    USER_ACCOUNT_ROLES,
} from "shared/utils/userAccountSession";
import {
    createApiRequestError,
    getApiErrorCode,
    getApiErrorMessage,
} from "shared/api/errors";

export type BiziboxCompanyImportCandidate = {bci: string; organization_number: string; name: string};
export type BiziboxCompanyImportPreview = {candidates: BiziboxCompanyImportCandidate[]; expected: number; previewKey: string; skipped: Record<string, number>};

const CASHFLOW_FORECAST_PENDING_MESSAGE = "נתוני התזרים המבוקשים עדיין לא עודכנו. התחזית תוצג לאחר עדכון הנתונים.";
export const getCashflowForecastPendingMessage = (error: unknown): string | null =>
    (error as {status?: number} | null)?.status === 409
        && getApiErrorCode(error) === "CASHFLOW_FORECAST_RANGE_PENDING"
        ? CASHFLOW_FORECAST_PENDING_MESSAGE : null;

export {
    EMAIL_ALREADY_EXISTS_CODE,
    EMAIL_ALREADY_EXISTS_MESSAGE,
} from "shared/api/errors";


export type MeetingSummarySearchResult = {
    result_type: "summary" | "topic";
    record_id: number;
    summary_id: number | null;
    topic_id: number | null;
    topic_index: number | null;
    meeting_id: number;
    company_id: number;
    company_name: string | null;
    meeting_title: string | null;
    scheduled_at: string | null;
    recording_started_at: string | null;
    title: string | null;
    content: string | null;
    content_preview: string;
    distance: number;
    similarity: number;
    similarity_percent: number;
    gemini_embedding_updated_at?: string | null;
};


export type MeetingSummarySearchResponse = {
    query: string;
    model: string;
    dimensions: number;
    results: MeetingSummarySearchResult[];
};

export type MeetingRecordingTrack = {
    index: number;
    url: string;
    contentType: string | null;
};

export type MeetingRecordingPlayback = {
    tracks: MeetingRecordingTrack[];
    expiresInSeconds: number;
};

export type MeetingPreparationSourceWarning = {
    code: string;
    meeting_id: number;
    reason: string;
    message: string;
};

export type MeetingSummarySearchCompany = {
    id: string | number;
    name: string | null;
    organization_number?: string | null;
    business_id?: string | number | null;
    is_active?: boolean;
};

export type MeetingFeedbackSearchResult = {
    result_type: "feedback";
    record_id: number;
    feedback_id: number;
    meeting_id: number;
    company_id: number;
    company_name: string | null;
    meeting_title: string | null;
    scheduled_at: string | null;
    recording_started_at: string | null;
    title: string | null;
    content: string | null;
    content_preview: string;
    distance: number;
    similarity: number;
    similarity_percent: number;
    gemini_embedding_updated_at?: string | null;
};

export type MeetingFeedbackSearchResponse = {
    query: string;
    model: string;
    dimensions: number;
    results: MeetingFeedbackSearchResult[];
};

export type MeetingFeedbackSearchCompany = MeetingSummarySearchCompany;

export type KnowledgeBaseCategory = {
    id: number;
    edit_token?: string;
    name: string;
    is_active: boolean;
    records_count?: number;
    created_at?: string | null;
    updated_at?: string | null;
};

export type KnowledgeBaseEmbeddingStatus = "ready" | "pending" | "failed";

export type KnowledgeBaseRecord = {
    id: number;
    edit_token?: string;
    title: string;
    content: string;
    category_id: number | null;
    category_name: string | null;
    category: KnowledgeBaseCategory | null;
    tags: string[];
    is_active: boolean;
    created_at: string | null;
    updated_at: string | null;
    embedding_status: KnowledgeBaseEmbeddingStatus;
    embedding: {
        status: KnowledgeBaseEmbeddingStatus;
        model: string | null;
        dimensions: number | null;
        updated_at: string | null;
        error: string | null;
    };
};

export type KnowledgeBaseSearchResult = KnowledgeBaseRecord & {
    result_type: "knowledge_base";
    record_id: number;
    distance: number;
    similarity: number;
    similarity_percent: number;
    content_preview: string;
};

export type KnowledgeBaseSearchResponse = {
    query: string;
    model: string;
    dimensions: number;
    results: KnowledgeBaseSearchResult[];
};

export type KnowledgeBaseExport = {
    format_version: "hk_global_knowledge_base_v1";
    exported_at: string;
    model: string;
    dimensions: number;
    categories: Array<Pick<KnowledgeBaseCategory, "id" | "name" | "is_active">>;
    records: Array<{
        id: number;
        title: string;
        content: string;
        category: string | null;
        category_id: number | null;
        tags: string[];
        is_active: boolean;
        created_at: string | null;
        updated_at: string | null;
        embedding: KnowledgeBaseRecord["embedding"];
    }>;
};

export type KnowledgeBaseImportResponse = {
    format_version: "hk_global_knowledge_base_v1";
    mode: "create" | "upsert";
    categories_created: number;
    categories_updated: number;
    created: number;
    updated: number;
    failed: number;
    results: Array<{
        index: number;
        status: "success" | "failed";
        action?: "created" | "updated";
        record?: KnowledgeBaseRecord;
        title?: string | null;
        error?: string;
    }>;
};

type ManagedAssistant = {
    id?: string | number;
    name?: string;
    display_name?: string;
    [key: string]: any;
};

export interface BillingRecurringSale {
    recurring_sale_id: string;
    customer: string;
    amount: number;
    card_last4: string;
    recurring_day: number | null;
    next_charge: string | null;
    status: string;
    is_active: boolean;
    last_charge_date: string | null;
    last_charge_status: string | null;
    last_success_date: string | null;
    last_fail_date: string | null;
    last_fail_reason: string | null;
    fail_count: number;
    last_document_link: string | null;
    update_url: string | null;
    group_id: string | null;
    group_name: string | null;
    email: string | null;
    phone: string | null;
    label: string;
    companies?: Array<{
        id: number;
        name: string;
        billing_amount: number;
        billing_type: string | null;
        next_billing_date: string | null;
        last_billing_date: string | null;
    }>;
}

export interface BillingCollectionMoneySummary {
    count: number;
    amount: number;
}

export interface BillingCollectionTransaction {
    customer: string;
    customer_id: string | number | null;
    amount: number;
    type: "transfer" | "card";
    date: string | null;
    receipt: string | number | null;
    receipt_link: string;
}

export interface BillingCollectionForecastRow {
    recurring_sale_id?: string;
    company_id?: number;
    billing_account_id?: number | null;
    billing_account_name?: string;
    billing_account_payer?: string;
    name: string;
    amount: number;
    dateKey: string | null;
    dateLabel: string;
    phone?: string;
    kind: "recurring" | "transfer";
    lastBillingDate?: string | null;
    company_edit_tokens?: Record<string, string>;
}

export interface BillingUnassignedCompany {
    company_id: number;
    company_name: string;
    default_amount: number;
    phone: string;
    status: string | null;
}

export interface BillingAccount {
    id: number;
    type: "TRANSFER" | "IMMEDIATE" | string;
    name: string;
    payer_name: string;
    default_amount: number;
    charge_kind: "DIRECT" | "VARIABLE" | string | null;
    has_card_token: boolean;
    invoice_email: string;
    invoice_desc: string;
    notes: string;
    companies_count: number;
}

export interface BillingCardInput {
    cardNumber: string;
    expiry: string;
    cvv: string;
    holderId?: string;
    holderName?: string;
}

export interface BillingManualChargeRow {
    billing_account_id: number;
    company_id: number;
    company_ids: number[];
    company_names: string[];
    name: string;
    payer_name: string;
    amount: number | null;
    charge_kind: "DIRECT" | "VARIABLE" | string;
    has_card_token: boolean;
    invoice_email: string;
    invoice_desc: string;
    phone: string;
    month: string;
}

export interface BillingCollectionsDashboard {
    period: {
        from: string;
        to: string;
        today: string;
        monthStart: string;
        monthEnd: string;
    };
    payments: {
        total: number;
        count: number;
        cards: BillingCollectionMoneySummary;
        transfers: BillingCollectionMoneySummary;
        transactions: BillingCollectionTransaction[];
    };
    forecast: {
        total: number;
        collected: number;
        projected: number;
        daysLeft: number;
        activeOrders: number;
        upcoming: { count: number; total: number; rows: BillingCollectionForecastRow[] };
        transfers: { count: number; total: number; rows: BillingCollectionForecastRow[] };
        overdue: { count: number; total: number; rows: BillingCollectionForecastRow[] };
        compare: { prevLabel: string; prevFull: number; prevCount: number };
    };
    failedRecurring: {
        outstanding: { count: number; total: number; rows: any[] };
    };
    recurringSales: BillingRecurringSale[];
    billingAccounts: BillingAccount[];
    manualCharges: { count: number; known_total: number; month: string; rows: BillingManualChargeRow[] };
    unassignedCompanies: { count: number; total: number; rows: BillingUnassignedCompany[] };
    ignoredAccounts: Array<{ id: number; match: string | null; customer_id: number | null; reason: string | null }>;
    paymentPages: Array<{ name: string; group_id: string | null; payment_url: string | null }>;
    sourceErrors: Record<string, string>;
    generatedAt: string;
}

export interface BillingSaleHistory {
    recurring_sale_id: string;
    customer: string;
    history: Array<{
        date: string;
        amount: number;
        paid: boolean;
        reason: string;
        card_last4: string;
        document_link: string;
    }>;
}

export type ProcessStageStatus = 'not_started' | 'in_progress' | 'done';

export interface BusinessProcessStageTemplateLine {
    edit_token?: string;
    id: number;
    stage_template_id: number;
    content: string;
    order_index: number;
    ind_deleted: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface BusinessProcessStageTemplate {
    edit_token?: string;
    id: number;
    business_id: number;
    business_process_program_id: number;
    name: string;
    order_index: number;
    ind_deleted: boolean;
    created_at?: string;
    updated_at?: string;
    lines?: BusinessProcessStageTemplateLine[];
    program?: BusinessProcessProgram;
}

export interface BusinessProcessProgram {
    edit_token?: string;
    id: number;
    business_id: number;
    name: string;
    ind_deleted: boolean;
    created_at?: string;
    updated_at?: string;
    stage_templates?: BusinessProcessStageTemplate[];
}

export interface CompanyProcessStage {
    edit_token?: string;
    id: number;
    company_id: number;
    business_stage_template_id: number;
    status: ProcessStageStatus;
    order_index: number;
    meeting_id: number | null;
    ind_deleted: boolean;
    created_at?: string;
    updated_at?: string;
    template?: BusinessProcessStageTemplate;
    meeting?: any;
}

export interface CompanyWidgetBindingSchemaItem {
    binding_key: string;
    source_type: string;
    label?: string;
}

export interface WidgetTemplate {
    id: number;
    widget_type: string;
    name: string;
    allow_multiple: boolean;
    default_settings?: Record<string, any>;
    binding_schema?: CompanyWidgetBindingSchemaItem[];
    order_index: number;
    ind_deleted: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface CompanyWidget {
    id: number;
    company_id: number;
    widget_template_id: number;
    title: string;
    layout_area: string;
    order_index: number;
    settings?: Record<string, any>;
    ind_deleted: boolean;
    has_bindings?: boolean;
    template: WidgetTemplate;
    created_at?: string;
    updated_at?: string;
}

export interface ImportableWidgetCompany {
    id: number;
    name: string;
    organization_number?: string | null;
    widgets_count: number;
}

export interface WidgetBindingSourceType {
    source_type: string;
    label: string;
}

export interface WidgetBindingSource {
    id: number;
    source_type: string;
    label: string;
    payload: any;
}

export interface CompanyWidgetsBootstrap {
    catalog: WidgetTemplate[];
    widgets: CompanyWidget[];
    importable_companies: ImportableWidgetCompany[];
    supported_binding_source_types: WidgetBindingSourceType[];
}

export interface CashflowWidgetAccount {
    account_id: string;
    account_name: string;
    account_number: string | number | null;
    current_balance?: number;
    credit_limit?: number;
}

export type CashflowRisk = "ok" | "warn" | "bad";

export interface CashflowWidgetTransaction {
    source?: string;
    workspace_entry_id?: string;
    id: string;
    date: string;
    trans_date: number | null;
    date_display: string;
    original_date: number | null;
    original_date_display: string;
    account_id: string | null;
    account_name: string | null;
    description: string;
    category: string;
    category_key_id?: string | number | null;
    category_name?: string | null;
    amount: number;
    is_expense: boolean;
    is_carry: boolean;
    end_day_balance: number | null;
    balance_color: string | null;
    is_deviation: boolean;
}

export interface CashflowWidgetCarry {
    transactions: CashflowWidgetTransaction[];
    total_in: number;
    total_out: number;
    net: number;
    balance_after_carry: number;
}

export interface CashflowWidgetDay {
    date: string;
    trans_date: number | null;
    day_name: string;
    date_display: string;
    transactions: CashflowWidgetTransaction[];
    incoming_total: number;
    outgoing_total: number;
    end_day_balance: number | null;
    balance_color: string | null;
    risk: CashflowRisk;
    is_deviation: boolean;
}

export interface CashflowWidgetRangeSummary {
    range_days: number;
    end_balance: number;
    end_balance_date: string | null;
    end_balance_date_display: string | null;
    risk: CashflowRisk;
    risk_label: string;
    deviation_amount?: number | null;
    first_deviation_date: string | null;
    first_deviation_date_display: string | null;
}

export interface CashflowWidgetForecast {
    includes_workspace_entries?: boolean;
    workspace_entries?: {status: "included" | "blocked"; reason?: string; count: number};
    range_days_applied?: number;
    data_health?: BiziboxDataHealth | null;
    company_id: number;
    company_name: string;
    selected_account_id: string;
    accounts: CashflowWidgetAccount[];
    current_date: string;
    current_date_display: string;
    subtitle: string;
    last_updated_display: string;
    date_from: number;
    date_till: number;
    opening_balance: number;
    credit_limit: number;
    carry: CashflowWidgetCarry;
    days: CashflowWidgetDay[];
    ranges: Record<string, CashflowWidgetRangeSummary>;
}

export interface RecentTransactionsWidgetAccount {
    account_id: string;
    account_name: string;
    account_number: string | number | null;
    current_balance: number | null;
    credit_limit: number | null;
}

export interface RecentTransactionsWidgetTransaction {
    id: string;
    supplierTaxId?: string;
    asmachta?: string | number | null;
    trans_date: number | null;
    date_display: string;
    account_id: string | null;
    account_name: string | null;
    account_number: string | number | null;
    description: string;
    category: string;
    payment_desc: string;
    amount: number;
    is_expense: boolean;
    balance: number | null;
}

export interface RecentTransactionsWidgetDay {
    date: string;
    date_display: string;
    balance: number | null;
    transactions: RecentTransactionsWidgetTransaction[];
}

export interface RecentTransactionsWidgetData {
    company_id: number;
    selected_account_id: string;
    range_days: number;
    date_from: number;
    date_till: number;
    accounts: RecentTransactionsWidgetAccount[];
    balance: number | null;
    credit_limit: number;
    account_count: number;
    days: RecentTransactionsWidgetDay[];
    transaction_count: number;
    last_updated_at: string | null;
    last_updated_display: string;
}

export interface CreditCardBillingCycle {
    billingDate: string;
    total: number;
    count: number;
}

export interface CreditCardChargesCard {
    cardId: string;
    cardName: string | null;
    lastFour: string | null;
    issuer: string | null;
    bankAccountId: string;
    bankAccountName: string | null;
    billingDay: number | null;
    balanceLastUpdatedDate: string | null;
    isStale: boolean;
    openCycle: (CreditCardBillingCycle & {isEstimate: boolean}) | null;
    retainedCategoryAttributionUnavailable?: boolean;
    cyclesComplete?: boolean;
    /** Regular monthly billing cycles; immediate charges can have other dates in the same month. */
    cycles: CreditCardBillingCycle[];
}

export interface CreditCardCharge {
    supplierTaxId?: string;
    transId: string;
    cardId: string;
    purchaseDate: string | null;
    billingDate: string;
    isOpenCycle: boolean;
    merchantRaw: string;
    merchant: string;
    categoryKeyId: string | null;
    categoryName: string | null;
    section: string | null;
    amount: number;
    originalAmount: number | null;
    originalCurrency: string | null;
    installment: {number: number; of: number} | null;
    isRefund: boolean;
}

export interface CreditCardChargeCoverage {
    version: 1;
    perCardAttributionComplete?: boolean;
    retainedCategoryDayFacts?: number;
    denominator: "stored_snapshot_rows";
    providerTotal: null;
    openCatalogingTotal: null;
    sourceWindow: {fromMonth: string; toMonth: string} | null;
    requestedClosedMonths: number;
    oldestSourceUpdatedAt: string | null;
    newestSourceUpdatedAt: string | null;
    sourceCardRows: number;
    currentCardRows: number;
    returnedCharges: number;
    excluded: {
        accountOutsideCurrentScope: number;
        cardIdentityNotCurrent: number;
        cardAccountMismatch: number;
        missingIdentityOrBillingDate: number;
        duplicateCardIdentity: number;
        reportCategory: number;
        invalidAmount: number;
        outsideSelectedCycles: number;
    };
    cards: Array<{cardId: string; lastFour: string | null; sourceCardRows: number; returnedCharges: number}>;
    filters: {currentEligibleCards: true; reportCategories: boolean; billingCycles: true; eventStatus: false; pagination: false};
}

export interface RetainedCardCategoryTotal {
    companyAccountId: string;
    categoryId: string;
    categoryName: string;
    reportDay: string;
    reportPhase: string;
    total: number;
    totalIncome: number;
    totalExpense: number;
    cashflowKnownTotal: number;
    detailSuppressed: true;
    cardAttribution: "unavailable";
}

export interface CreditCardChargesData {
    companyId: number;
    asOfDate: string;
    timeZone: "Asia/Jerusalem";
    cards: CreditCardChargesCard[];
    /** Optional while older server releases are still in use. Counts only the bounded stored source. */
    coverage?: CreditCardChargeCoverage;
    retainedCategoryTotals?: RetainedCardCategoryTotal[];
    retainedTotals?: {total: number; totalIncome: number; totalExpense: number; cashflowKnownTotal: number; scope: "stored_category_day_facts"; cardAttribution: "unavailable"};
    /** All charges in the selected billing months, including immediate charges, plus the open cycle. */
    charges: CreditCardCharge[];
}

export type MonthlyProfitabilityMetricKey = "grossProfit" | "operationalDiff" | "monthlyDiff";

export interface MonthlyProfitabilityMonth {
    id: number;
    company_id: number;
    year_month_key: string;
    updated_at: string | null;
    revenue: number;
    metrics: Record<MonthlyProfitabilityMetricKey, number>;
}

export interface MonthlyProfitabilityResponse {
    company_id: number;
    months: MonthlyProfitabilityMonth[];
    last_updated_at: string | null;
}

export interface CreateBusinessProcessStagePayload {
    business_id: number;
    business_process_program_id?: number;
    process_program_id?: number;
    program_id?: number;
    name: string;
    lines?: Array<{
        content: string;
        order_index: number;
    }>;
}

export interface CreateBusinessProcessProgramPayload {
    business_id: number;
    name: string;
}

export interface UpdateBusinessProcessProgramPayload {
    edit_token?: string;
    name?: string;
}

export interface AddBusinessProcessStageLinePayload {
    content: string;
    order_index?: number;
}

export interface UpdateBusinessProcessStagePayload {
    lines?: Array<{id?: number; content: string; order_index?: number}>;
    edit_token?: string;
    name?: string;
    business_process_program_id?: number;
    process_program_id?: number;
    program_id?: number;
}

export interface UpdateBusinessProcessStageLinePayload {
    edit_token?: string;
    content?: string;
}

export interface ReorderBusinessProcessStagesPayload {
    edit_tokens?: Record<string, string>;
    stageIds: number[];
    business_process_program_id?: number;
    process_program_id?: number;
    program_id?: number;
}

export interface ReorderBusinessProcessStageLinesPayload {
    edit_tokens?: Record<string, string>;
    lineIds: number[];
}

export interface AddCompanyProcessStageFromTemplatePayload {
    company_id: number;
    business_stage_template_id: number;
    order_index?: number | null;
}

export interface UpdateCompanyProcessStagePayload {
    edit_token?: string;
    status?: ProcessStageStatus;
    meeting_id?: number | null;
}

export interface ReorderCompanyProcessStagesPayload {
    edit_tokens?: Record<string, string>;
    stageIds: number[];
}

type Tab = 'business' | 'meetings' | 'client' | 'metrics' | 'widgets' | 'preparation' | 'my-process' | 'company-files';

export interface CalendarWeekResponse {
    events: CalendarEventDto[];
}

export interface CompanyStorageFolder {
    id: string;
    companyId: number;
    name: string;
    metadata?: CompanyStorageFolderMetadata | null;
    createdAt: string;
    updatedAt: string;
    createdByUserId?: number | null;
    updatedByUserId?: number | null;
}

export interface CompanyStorageFolderMetadata {
    internalOnly?: boolean;
    [key: string]: any;
}

export interface CompanyStorageFile {
    id: string;
    folderId: string;
    companyId: number;
    name: string;
    originalName: string;
    objectName: string;
    contentType: string;
    size: number | null;
    status: 'pending' | 'ready';
    createdAt: string;
    updatedAt: string;
    uploadedAt?: string;
    createdByUserId?: number | null;
    uploadedByUserId?: number | null;
    updatedByUserId?: number | null;
}

export interface CompanyStorageUploadTarget {
    fileId: string;
    fileName: string;
    contentType: string;
    objectName: string;
    uploadUrl: string;
    method: 'PUT';
    headers: {
        'Content-Type': string;
    };
}

export interface FinalizeCompanyStorageUploadsResponse {
    completed: CompanyStorageFile[];
    failed: Array<{
        fileId: string;
        error: string;
    }>;
}

function resolveScope(): boolean {
    const {hostname} = window.location;
    const host = hostname.toLowerCase();
    if (host.includes("jampa")) {
        return true;
    }
    if (host === "localhost" || host === "127.0.0.1") {
        return false; //set to true for Jampa local development
    }
    return false;
}

export const IS_JAMPA: boolean = resolveScope();

function resolveApiUrl(): string {
    if (typeof window === "undefined") {
        return "https://api.hak.co.il";
    }

    const host = window.location.hostname.toLowerCase();

    if (host === "localhost" || host === "127.0.0.1") {
        // Local dev: REACT_APP_API_URL wins (e.g. the stage backend when no local server runs).
        const override = (process.env.REACT_APP_API_URL || "").trim();
        if (override) return override.replace(/\/+$/, "");
        return "http://localhost:8080";
    }

    // firebase.json uses the default Hosting site of .firebaserc's stage project.
    // Its built-in domains must select the same API as the custom staging domains.
    const isStage = host.startsWith("stg.") ||
        host === "hk-stage-462507.web.app" ||
        host === "hk-stage-462507.firebaseapp.com";

    if (isStage) {
        return IS_JAMPA
            ? "https://jampa-api-stg.jampa.ai"
            : "https://api-stg.hak.co.il";
    }

    return IS_JAMPA
        ? "https://jampa-api.jampa.ai"
        : "https://api.hak.co.il";
}

export const API_URL: string = resolveApiUrl();


export const apiPath = (path: string) =>
    `${API_URL.replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;



const getValidToken = (): string | null => {
    const token = localStorage.getItem("authToken");
    if (!token) return null;

    try {
        const decoded: { exp: number } = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);
        if (decoded.exp - 300 > now) {
            return token;
        }
    } catch (e) {
        console.warn("Invalid token:", e);
    }

    return null;
};

const inFlightGetRequests = new Map<string, Promise<unknown>>();

export type SyncCompanyContactsResponse = {
    success: boolean;
    message?: string;
    data?: unknown;
};

export type CurrentUserCompanyStatus = CompanyStatus & {
    id: string | number;
    is_active: boolean;
    created_date: string;
    updated_date: string;
    operator_user_id: number | null;
    [key: string]: unknown;
};

export type CurrentUserCompanyDetails = CompanyDetails & {
    cashflow_report_string: string | null;
    status: string | null;
    cashflow_insighter_response: unknown | null;
    metadata: unknown | null;
    [key: string]: unknown;
};

export type CurrentUserCompanyConsultantUiStatus = {
    ui_status_key:
        | "WAITING_FOR_MEETING"
        | "WAITING_FOR_RESCHEDULE"
        | "WAITING_FOR_SCHEDULING";
    ui_status_name: string;
    ui_status_color: string;
    ui_color: string;
    next_meeting_text: string | null;
    cta: null | {
        type: "MEETING_PREP" | "EDIT_LAST_MEETING" | "CREATE_MEETING";
        label: string;
        meeting?: Meeting;
        meeting_id?: number;
    };
};

export type CurrentUserCompany = {
    id: string | number;
    name: string | null;
    organization_number: string | null;
    thread_id: string | null;
    product_type: string | null;
    is_active: boolean;
    vector_store_id: string | null;
    is_ai_active: boolean;
    ai_stop_till_date: string | null;
    occupation: string | null;
    customer_type: string | null;
    arrival_reason: string | null;
    central_goals: unknown[];
    process_approach: string | null;
    billing_amount: number | null;
    status: CurrentUserCompanyStatus | null;
    status_description: string | null;
    done_date: string | null;
    business_id: string | number | null;
    bci: string | null;
    is_bizibox_active: boolean;
    reports_thread_id: string | null;
    next_billing_date: string | null;
    last_billing_date: string | null;
    billing_type: string | null;
    subscription_id: string | null;
    billing_account_id: string | number | null;
    trial_end_date: string | null;
    last_four_digits: string | null;
    consultant_operator_user_id: string | number | null;
    ops_manager_id?: string | number | null;
    ops_manager_name?: string | null;
    hk_consultant_operator_user_id: string | number | null;
    is_show_client_thread: boolean;
    meeting_preparation: unknown | null;
    strengthening_event_interval_days: number | null;
    strengthening_event_last_run_at: string | null;
    future_cashflow_start_balance: number;
    future_cashflow_targets_start_month: string | null;
    future_cashflow_targets_end_month: string | null;
    created_at: string;
    updated_at: string;
    company_details: CurrentUserCompanyDetails | null;
    consultant_ui_status: CurrentUserCompanyConsultantUiStatus | null;
    products: string[];
    [key: string]: unknown;
};

export type CurrentUserBusiness = {
    id: string | number;
    business_name: string;
    logo?: string | null;
    business_type_id?: string | number | null;
    is_active?: boolean;
    created_at?: string;
    updated_at?: string;
    [key: string]: unknown;
};

export type CurrentBusinessLogo = {
    business_id: string | number;
    business_name: string;
    logo: string | null;
};

export type CurrentUserAccessProfile = {
    user: {
        authUserAccountId: number;
        legacyUserId: number;
        email: string;
        firstName: string | null;
        lastName: string | null;
        jobTitleId: string;
        businessId: number | null;
        companySelectedId: number | null;
        visibleScreens: string[];
        isActive: boolean;
        updatedAt: string | null;
    };
    access: {
        role: string;
        activeOnly: boolean;
        companyIds: number[];
        businessIds: number[];
    };
    capabilities: {
        canViewAllBusinesses: boolean;
        canViewAllCompanies: boolean;
        canViewAllActiveCompanies: boolean;
        canAccessAdminSettings: boolean;
        canAccessGlobalAdminTools: boolean;
        canViewBusinessWorkspace: boolean;
        canFilterBusinesses: boolean;
        canFilterOperators: boolean;
        canAccessLeads: boolean;
        canAccessBillingCollections: boolean;
        canManageBusinesses: boolean;
        canManageOwnBusiness: boolean;
        canManageCompanies: boolean;
        canManageHkConsultantAssignment: boolean;
        canSendCompanyReports: boolean;
        isCustomer: boolean;
    };
    refreshedAt: string;
};

const ACCESS_PROFILE_BOOLEAN_CAPABILITIES: Array<keyof CurrentUserAccessProfile["capabilities"]> = [
    "canViewAllBusinesses",
    "canViewAllCompanies",
    "canViewAllActiveCompanies",
    "canAccessAdminSettings",
    "canAccessGlobalAdminTools",
    "canViewBusinessWorkspace",
    "canFilterBusinesses",
    "canFilterOperators",
    "canAccessLeads",
    "canAccessBillingCollections",
    "canManageBusinesses",
    "canManageOwnBusiness",
    "canManageCompanies",
    "canManageHkConsultantAssignment",
    "canSendCompanyReports",
    "isCustomer",
];

function isRecord(value: unknown): value is Record<string, any> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPositiveInteger(value: unknown): boolean {
    return Number.isInteger(value) && Number(value) > 0;
}

function isPositiveIntegerArray(value: unknown): value is number[] {
    return Array.isArray(value) && value.every(isPositiveInteger);
}

function isCurrentUserAccessProfile(value: unknown): value is CurrentUserAccessProfile {
    if (!isRecord(value) || !isRecord(value.user) || !isRecord(value.access) || !isRecord(value.capabilities)) {
        return false;
    }

    const {user, access, capabilities} = value;
    const normalizedRole = normalizeUserAccountRole(access.role);
    const knownRole = Object.values(USER_ACCOUNT_ROLES).includes(normalizedRole as any);
    const normalizedUserRole = normalizeUserAccountRole(user.jobTitleId);
    const validNullablePositiveId = (id: unknown) => id == null || isPositiveInteger(id);

    return (
        isPositiveInteger(user.authUserAccountId) &&
        isPositiveInteger(user.legacyUserId) &&
        typeof user.email === "string" &&
        user.email.trim().length > 0 &&
        (user.firstName == null || typeof user.firstName === "string") &&
        (user.lastName == null || typeof user.lastName === "string") &&
        knownRole &&
        normalizedUserRole === normalizedRole &&
        validNullablePositiveId(user.businessId) &&
        validNullablePositiveId(user.companySelectedId) &&
        Array.isArray(user.visibleScreens) &&
        user.visibleScreens.every((screen: unknown) => typeof screen === "string") &&
        typeof user.isActive === "boolean" &&
        (user.updatedAt == null || typeof user.updatedAt === "string") &&
        typeof access.activeOnly === "boolean" &&
        isPositiveIntegerArray(access.companyIds) &&
        isPositiveIntegerArray(access.businessIds) &&
        (user.companySelectedId == null || access.companyIds.includes(user.companySelectedId)) &&
        ACCESS_PROFILE_BOOLEAN_CAPABILITIES.every(
            capability => typeof capabilities[capability] === "boolean"
        ) &&
        typeof value.refreshedAt === "string"
    );
}

function dedupeInFlight<T>(key: string, loader: () => Promise<T>): Promise<T> {
    const existingRequest = inFlightGetRequests.get(key);
    if (existingRequest) {
        return existingRequest as Promise<T>;
    }

    const request = loader().finally(() => {
        inFlightGetRequests.delete(key);
    });

    inFlightGetRequests.set(key, request);
    return request;
}

async function fetchCreditCardCharges(
    companyId: number | string, params?: {months?: number}, setup = false
): Promise<CreditCardChargesData> {
    const months = params?.months ?? 3;
    if (!Number.isInteger(months) || months < 1 || months > 12) {
        throw new Error("months must be an integer between 1 and 12");
    }
    const token = getValidToken();
    if (!token) throw new Error("Authentication token is missing or expired");
    return dedupeInFlight(`credit-card-charges:${setup ? "setup" : "report"}:${companyId}:${months}:${token}`, async () => {
        const response = await fetch(
            `${API_URL}/api/${setup ? "workspace/" : ""}companies/${encodeURIComponent(String(companyId))}/${setup ? "setup/" : ""}credit-card-charges?months=${months}`,
            {method: "GET", headers: {"Authorization": `Bearer ${token}`}}
        );
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.success !== true) {
            throw createApiRequestError(payload, response.status, "Failed to fetch credit card charges");
        }
        const data = payload.data;
        if (!data || String(data.companyId) !== String(companyId) ||
            typeof data.asOfDate !== "string" || data.timeZone !== "Asia/Jerusalem" ||
            !Array.isArray(data.cards) || !Array.isArray(data.charges)) {
            throw new Error("Invalid credit card charges response");
        }
        return data as CreditCardChargesData;
    });

}

export type MeetingRecordingSession = {
    recordingSessionId: string;
    recordingCapability: string;
    nextChunkIndex: number;
    state: 'open' | 'sealed' | 'completed' | 'aborted';
};

export const createApiClient = (clientScope: "legacy" | "workspace") => {
    const meetingsRoot = `${API_URL}/api/${clientScope === "workspace" ? "workspace/meetings" : "meetings"}`;
    const recordingSessionsRoot = `${API_URL}/api/workspace/meetings`;
    const meetingTasksRoot = `${API_URL}/api/${clientScope === "workspace" ? "workspace/meeting-tasks" : "tasks"}`;
    const scopedApiRoot = `${API_URL}/api/${clientScope === "workspace" ? "workspace/" : ""}`;
    const refreshMonthlyTargets = async (companyId: number | string, sync: boolean | "background") => {
        const token = getValidToken();
        const response = await fetch(`${scopedApiRoot}monthly-targets/refresh`, {
            method: "POST",
            headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
            body: JSON.stringify({company_id: Number(companyId), sync}),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok && response.status === 409 && getApiErrorCode(data) === "OPERATION_ALREADY_COMPLETED") {
            // The saved command outcome is definitive; retrieve its current
            // authorized projection instead of executing the command again.
            if (getValidToken() !== token) throw new Error("החשבון השתנה במהלך הרענון. יש לטעון מחדש את הנתונים");
            const readback = await fetch(`${scopedApiRoot}monthly-targets?company_id=${encodeURIComponent(String(companyId))}`, {
                method: "GET", headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
            });
            const current = await readback.json().catch(() => null);
            if (!readback.ok) throw createApiRequestError(current, readback.status, "לא ניתן לטעון את תוצאת הרענון");
            return current;
        }
        if (!response.ok) throw createApiRequestError(data, response.status, "לא ניתן לרענן כרגע את המדדים");
        return data;
    };
    const resolveStoredMonthId = async (monthIdValue: number, companyIdValue: unknown): Promise<number> => {
        const monthId = Number(monthIdValue);
        if (Number.isSafeInteger(monthId) && monthId > 0) return monthId;
        const companyId = Number(companyIdValue);
        const projection = -monthId;
        const year = Math.floor(projection / 100), month = projection % 100;
        if (!Number.isSafeInteger(monthId) || monthId >= 0 || !Number.isSafeInteger(companyId) || companyId <= 0
            || year < 1900 || year > 9999 || month < 1 || month > 12) throw new Error("לא נמצא חודש תקין לשמירה");
        // Presentation IDs never enter mutation payloads. Initialize only for
        // this explicit save, then resolve the exact authorized calendar month.
        const token = getValidToken();
        const initialized = await dedupeInFlight(`monthly-targets-initialize:${clientScope}:${companyId}:${token}`,
            () => refreshMonthlyTargets(companyId, false));
        if (getValidToken() !== token) throw new Error("החשבון השתנה במהלך השמירה. יש לטעון מחדש את הנתונים");
        const rows = initialized?.months ?? initialized?.data?.months;
        const stored = Array.isArray(rows) ? rows.find((row: any) => Number(row.company_id) === companyId
            && Number(row.year) === year && Number(row.month) === month && Number.isSafeInteger(Number(row.id)) && Number(row.id) > 0) : null;
        if (!stored) throw new Error("החודש טרם נוצר. יש לרענן את המדדים לפני השמירה");
        return Number(stored.id);
    };
    return {
    getBusinesses: async (businessId: string): Promise<any[]> => {
        try {
            const token = getValidToken();

            const response = await fetch(`${API_URL}/api/business/get-all-businesses`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({business_id: businessId})
            });
            if (!response.ok) {
                throw new Error("Failed to fetch businesses");
            }
            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error("Error fetching businesses:", error);
            return [];
        }
    },

    getAllBusinesses: async (params: { businessId: number | string }): Promise<
        | { success: true; data: any[] }
        | { success: false; message?: string; data?: any[] }
    > => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business/get-all-businesses`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ business_id: params.businessId }),
            });

            const data = await response.json().catch(() => null);

            if (!response.ok) {
                return {
                    success: false,
                    message: (data && (data.message || data.error)) || `Request failed (${response.status})`,
                    data: (data && data.data) || [],
                };
            }

            if (data?.success === true && Array.isArray(data?.data)) {
                return { success: true, data: data.data };
            }

            return {
                success: false,
                message: data?.message || data?.error || "Failed to fetch businesses",
                data: Array.isArray(data?.data) ? data.data : [],
            };
        } catch {
            return { success: false, message: "Network error", data: [] };
        }
    },

    deleteBusiness: async (businessId: number | string): Promise<{ success: boolean; message?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business/delete-business/${businessId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            const data = await response.json().catch(() => null);

            if (!response.ok) {
                return {
                    success: false,
                    message: (data && (data.message || data.error)) || `לא ניתן למחוק את העסק כרגע (${response.status}). אפשר לנסות שוב.`,
                };
            }

            if (data?.success === true) {
                return { success: true };
            }

            return { success: false, message: data?.message || data?.error || "מחיקת העסק לא אושרה. יש לטעון מחדש ולנסות שוב." };
        } catch {
            return { success: false, message: "לא התקבל אישור מהשרת. יש לטעון מחדש ולבדוק את מצב העסק." };
        }
    },

    setBusinessGuidelinesSender: async (businessId: string, values: Record<string, string>): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business/set-business-guidelines/${businessId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(values)
            });
            if (!response.ok) {
                throw new Error("Failed to fetch businesses");
            }
            return await response.json();

        } catch (error) {
            console.error("Error fetching businesses:", error);
            return false;
        }
    },

    getBusinessGuidelines: async (businessId: string): Promise<Record<string, string>> => {
        try {
            const token = getValidToken();

            const response = await fetch(`${API_URL}/api/business/get-business-guidelines/${businessId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });
            if (!response.ok) {
                throw new Error("Failed to fetch businesses");
            }
            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error("Error fetching businesses:", error);
            return {};
        }
    },

    getUserMessageTypes: async (userId: number): Promise<any[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/whatsapp/get-user-message-types`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({userId})
            });
            const data = await response.json();
            if (!response.ok || data.success === false) {
                throw Object.assign(new Error(data?.error?.message || data?.error || "לא ניתן לטעון את העדפות ההודעות"), {
                    status: response.status, code: data?.code || data?.error?.code,
                });
            }
            if (data.success && Array.isArray(data.data)) {
                return data.data.map((item: any) => ({
                    user_id: item.user_id,
                    message_type_id: item.message_type_id,
                    name: item.name,
                    is_active: item.is_active,
                    ...(typeof item.edit_token === "string" ? {edit_token: item.edit_token} : {}),
                }));
            } else {
                return [];
            }
        } catch (error) {
            console.error("Error fetching user message types:", error);
            throw error;
        }
    },

    getUserMessageTypesByCompanyId: async (companyId: number): Promise<any[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/whatsapp/get-user-message-types-by-company`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({companyId})
            });
            if (!response.ok) {
                throw new Error("Failed to fetch user message types");
            }
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                return data.data.map((item: any) => ({
                    user_id: item.user_id,
                    message_type_id: item.message_type_id,
                    name: item.name,
                    is_active: item.is_active,
                }));
            } else {
                return [];
            }
        } catch (error) {
            console.error("Error fetching user message types:", error);
            return [];
        }
    },

    setUserMessageTypes: async (userId: number, messageTypes: any[]): Promise<any[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/whatsapp/set-user-message-types`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({userId, messageTypes})
            });
            const data = await response.json();
            if (!response.ok || data.success === false) {
                throw Object.assign(new Error(data?.error?.message || data?.error || "לא ניתן לשמור את העדפות ההודעות"), {
                    status: response.status, code: data?.code || data?.error?.code,
                });
            }
            return data.data;
        } catch (error) {
            console.error("Error setting user message types:", error);
            throw error;
        }
    },

    handleBudgetLogic: async (companyId: string): Promise<{ success: boolean }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies/handle-budget-logic`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({companyId})
            });

            if (!response.ok) {
                throw new Error("Failed to handle budget logic");
            }

            const data = await response.json();
            return {success: data.success};
        } catch (error) {
            console.error("❌ Error in handleBudgetLogic:", error);
            return {success: false};
        }
    },

    sendPdfToEmail: async (
        companyId: number,
        blob: Blob,
        subject: string,
        text: string,
        users: any,
        filename: string,
        includeChildren: boolean,
        startDate: any,
        endDate: any,
        reportType?: 'summary' | 'detailed' | 'excel',
        accountIds?: string[] | null,
        companyIds?: number[] | null
    ): Promise<any> => {
        try {
            const pdfFile = new File([blob], filename, {
                type: 'application/pdf',
            });

            const fd = new FormData();
            fd.append('pdf', pdfFile);
            fd.append('userIds', users);
            fd.append('subject', subject);
            fd.append('text', text);
            fd.append('startDate', startDate);
            fd.append('endDate', endDate);
            fd.append('reportType', reportType || 'summary');
            if (accountIds?.length) {
                fd.append('accountIds', JSON.stringify(accountIds));
            }
            if (companyIds?.length) {
                fd.append('companyIds', JSON.stringify(companyIds));
            }

            const token = getValidToken();
            const reportTarget = Number(companyId) > 0 ? String(companyId) : "multiple";
            const response = await fetch(`${API_URL}/api/companies/send-email-pdf/${reportTarget}`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`
                },
                body: fd,
            });

            if (!response.ok) throw new Error(await response.text());

            const data = await response.json();
            return {success: data.ok};
        } catch (error) {
            console.error("❌ Error in sendPdfToEmail:", error);
            return {success: false};
        }
    },

    fetchMessagesByCompanyId: async (companyId: string, type: Tab): Promise<Message[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/openai/get-messages`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({id: companyId, type: type})
            });

            if (!response.ok) {
                throw new Error("Failed to retrieve messages");
            }

            const data = await response.json(); // Parse JSON response

            // Validate response format
            if (!data.success || !Array.isArray(data.data)) {
                throw new Error("Invalid response format");
            }

            // Transform API response to match the frontend's required format
            return data.data.map((msg: any) => ({
                role: msg.role === "user" ? "user" : "assistant",
                content: msg.content[0]?.text?.value || "",
                id: msg.id,
                created_at: msg.created_at,
                metadata: msg.metadata ?? null,
                process_id: msg.process_id ?? null,
                thinking_process_status: msg.thinking_process_status ?? null,
                is_show_think_process: msg.role === "user" && msg.process_id && msg.thinking_process_status !== "completed"
            })).reverse();

        } catch (error) {
            console.error("Error fetching messages:", error);
            return []; // Return an empty array on failure
        }
    },

    // Legacy callers may inspect the candidates, but this GET never creates them.
    fetchNewCompanies: async (): Promise<BiziboxCompanyImportCandidate[]> => {
        const response = await fetch(`${API_URL}/api/companies/get-new-companies`, {
            headers: {Authorization: `Bearer ${getValidToken()}`}, cache: "no-store",
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(getApiErrorMessage(payload, "לא ניתן לטעון חברות חדשות"));
        return payload;
    },

    previewNewCompanies: async (): Promise<BiziboxCompanyImportPreview> => {
        const response = await fetch(`${API_URL}/api/companies/new-companies/preview`, {
            headers: {Authorization: `Bearer ${getValidToken()}`}, cache: "no-store",
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(getApiErrorMessage(payload, "לא ניתן לטעון את רשימת החברות ליבוא"));
        return payload;
    },

    importNewCompanies: async (input: {confirm: true; expected: number; previewKey: string; allowLargeImport: boolean}): Promise<{count: number; batchId: string}> => {
        const response = await fetch(`${API_URL}/api/companies/new-companies/import`, {
            method: "POST", headers: {Authorization: `Bearer ${getValidToken()}`, "Content-Type": "application/json"},
            body: JSON.stringify(input),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(getApiErrorMessage(payload, "יבוא החברות לא הושלם"));
        return {count: payload.count, batchId: payload.batchId};
    },

    getCompanyStatuses: async (): Promise<CompanyStatus[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-company-statuses`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });

        const data = await response.json();
        return data.map((status: any) => ({
            status: status.status,
            status_name: status.status_name,
            color: status.color
        }));
    },

    syncCompanyContacts: async (
        companyId: string | number
    ): Promise<SyncCompanyContactsResponse> => {
        const normalizedCompanyId = Number(companyId);
        if (!Number.isFinite(normalizedCompanyId)) {
            return {success: false, message: "Invalid company id"};
        }

        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-contacts/sync-company`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({company_id: normalizedCompanyId})
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok || data?.success === false) {
                return {
                    success: false,
                    message: data?.message || data?.error || `Failed to sync company contacts (${response.status})`,
                    data: data?.data,
                };
            }

            return {
                success: true,
                message: data?.message,
                data: data?.data,
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("Error syncing company contacts:", message);
            return {success: false, message};
        }
    },

    addUser: async (
        userData: {
            first_name: string | null;
            last_name: string | null;
            phone_number: string | null;
            company_id: number;
            email: string | null;
            is_voice_messages: boolean;
            is_whatsapp_to_ai: boolean;
            is_main_user?: boolean;
            contact_role?: string | null;
            group_name?: string | null;
            group_id?: string | null;
            visible_screens?: string[];
            gender?: string | null;
            birth_date?: string | null;
            marital_status?: string | null;
            professional_occupation?: string | null;
        }
    ): Promise<{ success: boolean; message: string; data?: any; code?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/${shouldUseUserAccountsExperience(decodeAuthTokenClaims(token)) ? "user-accounts/company-contacts/add" : "users/add-user"}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(userData)
            });

            const data = await response.json().catch(() => null) as any;
            const code = getApiErrorCode(data);

            if (!response.ok || data?.success === false) {
                return {
                    success: false,
                    message: getApiErrorMessage(data, "Failed to add user"),
                    ...(code ? {code} : {}),
                };
            }

            if (data?.success) {
                return {
                    success: true,
                    message: typeof data.message === "string" ? data.message : "User added successfully",
                    data: data.data,
                    ...(code ? {code} : {}),
                };
            }

            return {success: false, message: "Failed to add user"};

        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("Error adding user:", errorMessage);
            return {success: false, message: errorMessage};
        }
    },

    editUser: async (
        userData: {
            userId: string;
            companyId?: number | string;
            editToken?: string | null;
            firstName: string | null;
            lastName: string | null;
            phone: string | null;
            email: string | null;
            role: string;
            isVoiceMessage: boolean
            isWhatsappToAi: boolean
            isMainUser?: boolean
            contactRole?: string | null
            groupName?: string | null
            groupId?: string | null
            visibleScreens?: string[]
            gender?: string | null
            birthDate?: string | null
            maritalStatus?: string | null
            professionalOccupation?: string | null
        }
    ): Promise<{ success: boolean; message: string; code?: string; status?: number }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/${shouldUseUserAccountsExperience(decodeAuthTokenClaims(token)) ? "user-accounts/company-contacts/edit" : "users/edit-user"}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    userId: userData.userId,
                    ...(userData.companyId === undefined ? {} : {company_id: userData.companyId}),
                    ...(userData.editToken === undefined ? {} : {edit_token: userData.editToken}),
                    first_name: userData.firstName,
                    last_name: userData.lastName,
                    email: userData.email,
                    phone_number: userData.phone,
                    is_voice_messages: userData.isVoiceMessage,
                    is_whatsapp_to_ai: userData.isWhatsappToAi,
                    is_main_user: userData.isMainUser,
                    contact_role: userData.contactRole,
                    group_name: userData.groupName,
                    group_id: userData.groupId,
                    visible_screens: userData.visibleScreens,
                    gender: userData.gender,
                    birth_date: userData.birthDate,
                    marital_status: userData.maritalStatus,
                    professional_occupation: userData.professionalOccupation,
                })
            });

            const data = await response.json().catch(() => null) as any;
            const code = getApiErrorCode(data);

            if (!response.ok || data?.success === false) {
                return {
                    success: false,
                    status: response.status,
                    message: getApiErrorMessage(data, "Failed to update user"),
                    ...(code ? {code} : {}),
                };
            }

            if (data?.success) {
                return {
                    success: true,
                    message: typeof data.message === "string" ? data.message : "User updated successfully",
                    ...(code ? {code} : {}),
                };
            }

            return {success: false, message: "Failed to update user"};
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("Error editing user:", errorMessage);
            return {success: false, message: errorMessage};
        }
    },

    deleteUser: async (userId: string | number): Promise<{ success: boolean; message: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/users/delete-user/${userId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error("Failed to delete user");
            }

            const data = await response.json().catch(() => ({}));
            if (data?.success === false) {
                return { success: false, message: data?.message || "Failed to delete user" };
            }
            return { success: true, message: data?.message || "User deleted successfully" };
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("Error deleting user:", errorMessage);
            return { success: false, message: errorMessage };
        }
    },

    getUsersByBusinessId: async (): Promise<User[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/users/get-users-by-business-id`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to retrieve users by getUsersByBusinessId. Status: ${response.status}`);
            }

            const data = await response.json();
            if (!data.success || !Array.isArray(data.data)) {
                throw new Error("Invalid response format: Expected an array");
            }

            return data.data.map((user: any) => ({
                id: Number(user.id),
                email: user.email,
                userId: user.userId,
                groupId: user.groupId,
                company_id: Number(user.company_id),
                first_name: user.first_name,
                last_name: user.last_name,
                job_title_id: user.job_title_id,
                phone_number: user.phone_number,
                is_message_contact: user.is_message_contact,
                is_voice_messages: user.is_voice_messages,
                is_whatsapp_to_ai: user.is_whatsapp_to_ai,
                is_main_user: user.is_main_user,
                contact_role: user.contact_role,
                group_name: user.group_name,
                group_id: user.group_id,
                visible_screens: user.visible_screens,
                gender: user.gender,
                birth_date: user.birth_date,
                marital_status: user.marital_status,
                professional_occupation: user.professional_occupation,
            }));

        } catch (error) {
            console.error("❌ Error fetching users by getUsersByBusinessId:", error);
            return [];
        }
    },

    /**
     * Strict operator loader for the User_Accounts workspace.
     *
     * The legacy getUsersByBusinessId contract intentionally converts errors
     * to an empty list. The new workspace must distinguish a real empty list
     * from a failed authorization/data request, so this additive method throws
     * on every non-success or malformed response.
     */
    getCurrentUserOperators: async (): Promise<User[]> => {
        const token = getValidToken();
        if (!token) {
            throw new Error("Authentication token is missing or expired");
        }

        const response = await fetch(`${API_URL}/api/users/get-users-by-business-id`, {
            method: "GET",
            cache: "no-store",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json().catch(() => null);

        if (!response.ok || data?.success !== true || !Array.isArray(data?.data)) {
            const message = data?.message || data?.error ||
                `Failed to fetch current user operators (${response.status})`;
            throw new Error(message);
        }

        return data.data.map((user: any) => {
            const id = Number(user?.id);
            const email = typeof user?.email === "string" ? user.email.trim() : "";
            if (!Number.isInteger(id) || id <= 0 || !email) {
                throw new Error("The current user operators response contains an invalid user");
            }

            return {
                id,
                email,
                userId: user.userId,
                groupId: user.groupId,
                company_id: user.company_id == null ? undefined : Number(user.company_id),
                first_name: user.first_name,
                last_name: user.last_name,
                job_title_id: user.job_title_id,
                phone_number: user.phone_number,
                is_message_contact: user.is_message_contact,
                is_voice_messages: user.is_voice_messages,
                is_whatsapp_to_ai: user.is_whatsapp_to_ai,
                is_main_user: user.is_main_user,
                contact_role: user.contact_role,
                group_name: user.group_name,
                group_id: user.group_id,
                visible_screens: user.visible_screens,
                gender: user.gender,
                birth_date: user.birth_date,
                marital_status: user.marital_status,
                professional_occupation: user.professional_occupation,
            };
        });
    },

    getUsersByCompany: async (companyId: string): Promise<User[]> => {
        if (!companyId) {
            console.error("⚠ No companyId provided to getUsersByCompany.");
            return [];
        }

        try {
            console.log(`�� Sending request to get users for companyId: ${companyId}`);
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/${shouldUseUserAccountsExperience(decodeAuthTokenClaims(token)) ? "user-accounts/company-contacts/list" : "users/get-users-by-company"}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({company_id: companyId}) // ✅ Ensure request body matches backend expectation
            });

            if (!response.ok) {
                throw new Error(`Failed to retrieve users: ${response.statusText}`);
            }

            const users = await response.json(); // ✅ API returns an array directly, no `data` field
            console.log(`✅ Received users:`, users);

            if (!Array.isArray(users)) {
                throw new Error("Invalid response format: Expected an array");
            }

            return users.map((user: any) => ({
                id: Number(user.id), // Legacy company contact key for existing editing/message APIs.
                user_id: user.user_id == null ? null : Number(user.user_id),
                user_account_id: user.user_account_id == null ? null : Number(user.user_account_id),
                contact_id: user.contact_id == null ? null : Number(user.contact_id),
                contact_user_id: user.contact_user_id == null ? null : Number(user.contact_user_id),
                email: user.email,
                userId: user.userId,
                groupId: user.groupId,
                company_id: Number(user.company_id), // ✅ Ensure `company_id` is a number
                first_name: user.first_name,
                last_name: user.last_name,
                job_title_id: user.job_title_id,
                phone_number: user.phone_number,
                is_message_contact: user.is_message_contact,
                is_voice_messages: user.is_voice_messages,
                is_whatsapp_to_ai: user.is_whatsapp_to_ai,
                is_main_user: user.is_main_user,
                contact_role: user.contact_role,
                group_name: user.group_name,
                group_id: user.group_id,
                visible_screens: user.visible_screens,
                gender: user.gender,
                birth_date: user.birth_date,
                marital_status: user.marital_status,
                professional_occupation: user.professional_occupation,
                ...(Object.prototype.hasOwnProperty.call(user, "edit_token") ? {edit_token: user.edit_token} : {}),
                ...(Object.prototype.hasOwnProperty.call(user, "last_login_at") ? {last_login_at: user.last_login_at} : {}),
            }));

        } catch (error) {
            console.error("❌ Error fetching users:", error);
            return [];
        }
    },

    getManageMessagesUsersList: async (state?: "ai" | "no-ai" | null): Promise<any[]> => {
        try {
            const token = getValidToken();
            const url = `${API_URL}/api/manage-messages/get-users-list/${state ?? "all"}`;
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch manage-messages users list. Status: ${response.status}`);
            }
            const data = await response.json();
            const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
            return list;
        } catch (error) {
            console.error("Error fetching manage-messages users list:", error);
            return [];
        }
    },

    getManageMessagesList: async (userId?: any, companyId?: any, phone?: any): Promise<any[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/manage-messages/get-messages-list/${userId}/${companyId}/${phone}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch messages for user ${userId}. Status: ${response.status}`);
            }
            const data = await response.json();
            const rawList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
            // New API returns [{ chat_message_json: {...} }]; flatten to list of message objects
            const list = rawList.map((item: any) =>
                item?.chat_message_json != null ? item.chat_message_json : item
            );
            return list;
        } catch (error) {
            console.error("Error fetching manage-messages messages list:", error);
            return [];
        }
    },

    getManagedAssistants: async (): Promise<ManagedAssistant[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/assistants/get-managed-assistants`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch managed assistants. Status: ${response.status}`);
            }
            const data = await response.json();
            const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
            return list;
        } catch (error) {
            console.error("Error fetching managed assistants:", error);
            return [];
        }
    },

    getAssistantDetails: async (id: string | number): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/assistants/get-assistant-details/${id}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch assistant details. Status: ${response.status}`);
            }
            const data = await response.json();
            return data?.data ?? data;
        } catch (error) {
            console.error("Error fetching assistant details:", error);
            throw error;
        }
    },

    updateAssistant: async (
        id: string | number,
        payload: {
            description: string;
            model: string;
            temperature: number | string;
            functions_declerations?: string | object;
            edit_token?: unknown;
        }
    ): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/assistants/update-assistant/${id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const failure = await response.json().catch(() => null);
                throw createApiRequestError(failure, response.status, "Failed to update assistant");
            }
            const data = await response.json();
            return data?.data ?? data;
        } catch (error) {
            console.error("Error updating assistant:", error);
            throw error;
        }
    },

    deleteMessage: async (companyId: string, messageId: string): Promise<{
        id: string;
        object: string;
        deleted: boolean
    }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/openai/delete-message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({companyId, messageId})
            });

            if (!response.ok) {
                throw new Error(`Failed to delete message. Status: ${response.status}`);
            }

            const data = await response.json(); // Parse JSON response

            // Validate response structure
            if (!data || typeof data !== "object" || !data.deleted) {
                throw new Error("Invalid response format");
            }

            // Return the expected structure
            return {
                id: data.id,
                object: data.object,
                deleted: data.deleted
            };

        } catch (error) {
            console.error("Error deleting message:", error);
            return {id: messageId, object: "thread.message.deleted", deleted: false}; // Fail gracefully
        }
    },

    sendMessageToCompany: async (companyId: string, message: string, type: string): Promise<any> => {
        try {
            console.log('Sending message to thread called: ', new Date().toLocaleTimeString());
            const token = getValidToken();
            let userId: number | null = null;
            if (token) {
                try {
                    const decoded: any = jwtDecode(token);
                    // Try common claim names for user id
                    userId = decoded.user_id;
                    if (typeof userId === "string") {
                        userId = parseInt(userId, 10);
                    }
                } catch (e) {
                    console.warn("Could not decode token to extract userId:", e);
                }
            }
            const response = await fetch(`${API_URL}/api/chat/handle-chat`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({companyId, userMessageContent: message, userId, type, entryPoint: 'ai_chat'})
            });

            console.log('Sending message to thread finished: ', new Date().toLocaleTimeString());

            if (!response) {
                throw new Error("Failed to retrieve message");
            }

            return await response.json();

        } catch (error) {
            console.error("Error sending message:", error);
            throw error;
        }
    },

    executeRun: async (
        companyId: string,
        originalPrompt: string,
        onStreamUpdate: (text: string) => void
    ): Promise<void> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/openai/execute-run`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({companyId, originalPrompt})
            });

            if (!response.ok) {
                throw new Error(`Request failed. Status: ${response.status}`);
            }

            const data = await response.json();
            if (data.text) {
                onStreamUpdate(data.text);
            }
        } catch (error) {
            console.error("❌ Error executing run:", error);
        }
    },

    updateCompany: async (companyData: {
        id: number;
        name?: string;
        occupation?: string;
        billing_amount?: string;
        ops_manager_id?: string | number | null;
        hk_consultant_operator_user_id?: string;
        status?: CompanyStatus;
        status_description?: string;
    }): Promise<{ success: boolean; message: string; updatedCompany?: Company }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies/update-company`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    id: companyData.id,
                    name: companyData.name,
                    occupation: companyData.occupation || null,
                    billing_amount: companyData.billing_amount || null,
                    ...(companyData.ops_manager_id !== undefined ? {
                        ops_manager_id: companyData.ops_manager_id,
                    } : {}),
                    ...(companyData.hk_consultant_operator_user_id !== undefined ? {
                        hk_consultant_operator_user_id:
                            Number(companyData.hk_consultant_operator_user_id) || null,
                    } : {}),
                    status: companyData?.status || null,
                    status_description: companyData.status_description || null,
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to update company. Status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message,
                    updatedCompany: data.updatedCompany
                };
            } else {
                return {
                    success: false,
                    message: "Failed to update company"
                };
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("❌ Error updating company:", errorMessage);
            return {
                success: false,
                message: errorMessage
            };
        }
    },

    updateCompanyStatus: async (companyId: number, status: CompanyStatus): Promise<{
        success: boolean;
        message: string;
        updatedCompany?: Company
    }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies/update-company-status`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    id: companyId,
                    status: status.status || null,
                    status_description: status.status_name || null,
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to update company. Status: ${response.status}`);
            }

            const text = await response.text();
            const data = text ? JSON.parse(text) : null;

            if (!data || data.success) {
                return {
                    success: true,
                    message: data?.message || "Company status updated successfully",
                    updatedCompany: data?.updatedCompany
                };
            } else {
                return {
                    success: false,
                    message: "Failed to update company status"
                };
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("❌ Error updating company status:", errorMessage);
            console.log("errorMessage is ", errorMessage);
            return {
                success: false,
                message: errorMessage
            };
        }
    },

    updateCashFlowDetails: async (companyId: string, isUpdateFile: boolean): Promise<CompanyDetails> => {
        try {
            console.log(`�� Updating Cash Flow File for companyId: ${companyId}...`);

            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies-files/update-cash-flow-details`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({companyId, isUpdateFile}) // ✅ Sending companyId as payload
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to update Cash Flow File. Status: ${response.status}. Server says: ${errorText}`);
            }

            const data: CompanyDetails = await response.json();

            console.log("✅ Cash Flow File updated successfully:", data);

            return data;

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("❌ Error updating Cash Flow File:", errorMessage);
            throw error;
        }
    },

    updateCashFlowDetailsByCompanyId: async (
        companyId: string | number
    ): Promise<{ success: boolean; data?: { cashflowFinalString?: string; budgetFinalString?: string } }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies-files/update-cash-flow-details/${companyId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error(`Failed to update cash flow details. Status: ${response.status}`);
            }
            const data: { success: boolean; data?: { cashflowFinalString?: string; budgetFinalString?: string } } =
                await response.json();
            return data;
        } catch (error) {
            console.error("Error updating cash flow details:", error);
            throw error;
        }
    },

    updateTotals: async (
        companyId: number,
        notAdjustedMinTotal: number,
        nigrarotMinTotal: number,
        nigrarotMinDay: number,
        gapMinTotal: number,
        paymentCollectionMinTotal: number,
        paymentCollectionMinDays: number
    ): Promise<CompanyDetails> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies-files/update-totals`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    companyId,
                    notAdjustedMinTotal,
                    nigrarotMinTotal,
                    nigrarotMinDay,
                    gapMinTotal,
                    paymentCollectionMinTotal,
                    paymentCollectionMinDays,
                })
            });
            if (!response.ok) {
                throw new Error(`Failed to update totals. Status: ${response.status}`);
            }
            const data: CompanyDetails = await response.json();

            return data;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("❌ Error updating totals:", errorMessage);
            throw error;
        }
    },

    getActiveCompanies: async (options: {includeArchived?: boolean} = {}): Promise<Company[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-active-companies${options.includeArchived ? "?includeArchived=true" : ""}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });

        const data = await response.json().catch(() => null);
        if (!response.ok || !Array.isArray(data)) throw new Error(data?.message || "לא ניתן לטעון את רשימת החברות");

        // Map each item to a Company, converting string fields to numbers as needed.
        const result = data
            .map((company: any) => ({
                ...company,
            }))
            .filter((company: Company) => company.is_active !== false);

        // console.log(JSON.stringify(result, null, 2));
        return result;
    },

    getCurrentUserCompanies: async (options: {includeArchived?: boolean; signal?: AbortSignal} = {}): Promise<Company[]> => {
        const token = getValidToken();
        if (!token) {
            throw new Error("Authentication token is missing or expired");
        }

        const response = await fetch(`${API_URL}/api/user-accounts/current-user/companies${options.includeArchived ? "?includeArchived=true" : ""}`, {
            method: "GET",
            signal: options.signal,
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json().catch(() => null);

        if (!response.ok || !Array.isArray(data)) {
            const message = data?.message || data?.error ||
                `Failed to fetch current user companies (${response.status})`;
            throw new Error(message);
        }

        return data as Company[];
    },

    getCurrentUserBusinesses: async (): Promise<CurrentUserBusiness[]> => {
        const token = getValidToken();
        if (!token) {
            throw new Error("Authentication token is missing or expired");
        }

        const response = await fetch(`${API_URL}/api/user-accounts/current-user/businesses`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json().catch(() => null);

        if (!response.ok || !Array.isArray(data)) {
            const message = data?.message || data?.error ||
                `Failed to fetch current user businesses (${response.status})`;
            throw new Error(message);
        }

        return data as CurrentUserBusiness[];
    },

    getCurrentBusinessLogo: async (): Promise<CurrentBusinessLogo> => {
        const token = getValidToken();
        if (!token) {
            throw new Error("Authentication token is missing or expired");
        }

        const response = await fetch(`${API_URL}/api/business/logo`, {
            method: "GET",
            cache: "no-store",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });
        const payload = await response.json().catch(() => null);
        const data = payload?.data ?? payload;
        if (!response.ok || !data || typeof data !== "object") {
            throw new Error(payload?.message || payload?.error || "לא ניתן לטעון את לוגו המשרד");
        }
        return data as CurrentBusinessLogo;
    },

    updateCurrentBusinessLogo: async (logo: string | null): Promise<CurrentBusinessLogo> => {
        const token = getValidToken();
        if (!token) {
            throw new Error("Authentication token is missing or expired");
        }

        const response = await fetch(`${API_URL}/api/business/logo`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({logo}),
        });
        const payload = await response.json().catch(() => null);
        const data = payload?.data ?? payload;
        if (!response.ok || !data || typeof data !== "object") {
            throw new Error(payload?.message || payload?.error || "שמירת לוגו המשרד נכשלה");
        }
        return data as CurrentBusinessLogo;
    },

    getCurrentUserAccessProfile: async (): Promise<CurrentUserAccessProfile> => {
        const token = getValidToken();
        if (!token) {
            throw new Error("Authentication token is missing or expired");
        }

        return dedupeInFlight(`current-user-access-profile:${token}`, async () => {
            const response = await fetch(
                `${API_URL}/api/user-accounts/current-user/access-profile`,
                {
                    method: "GET",
                    cache: "no-store",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                }
            );
            const data = await response.json().catch(() => null);

            if (!response.ok || !isCurrentUserAccessProfile(data)) {
                const message = data?.message || data?.error ||
                    `Failed to fetch current user access profile (${response.status})`;
                throw new Error(message);
            }

            return data;
        });
    },

    memory: async (params: any): Promise<any> => {
        const token = getValidToken();
        return await fetch(`${API_URL}/api/metrics/memory?${params.toString()}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
    },


    getServerIp: async (): Promise<string | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies/get-server-ip`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch server IP. Status: ${response.status}`);
            }

            const data = await response.json();

            if (!data || typeof data.ip !== "string") {
                throw new Error("Invalid response format: Expected an object with an 'ip' string");
            }

            return data.ip; // ✅ Return the server IP as a string
        } catch (error) {
            console.error("❌ Error fetching server IP:", error);
            return null; // Return null on failure
        }
    },

    showAllowedCategories: async (eventId: number): Promise<{ success: boolean, data?: any }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/events/show-allowed-categories/${eventId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            if (!response.ok) {
                throw new Error("Failed to update event status");
            }

            const data = await response.json();
            return {success: data.success, data: data.data};
        } catch (error) {
            console.error("❌ Error setting event status:", error);
            return {success: false};
        }
    },

    showCategorySuggestion: async (eventId: number): Promise<{ success: boolean, data?: any }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/events/show-category-suggestion/${eventId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            if (!response.ok) {
                throw new Error("Failed to update event status");
            }

            const data = await response.json();
            return {success: data.success, data: data.data};
        } catch (error) {
            console.error("❌ Error setting event status:", error);
            return {success: false};
        }
    },

    showHistoryTransactions: async (eventId: number): Promise<{ success: boolean; data?: any }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/events/show-history-transactions/${eventId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch history transactions");
            }

            const data = await response.json();
            if (data && typeof data === "object" && "data" in data) {
                return {success: data.success !== false, data: data.data};
            }
            return {success: true, data};
        } catch (error) {
            console.error("❌ Error fetching history transactions:", error);
            return {success: false};
        }
    },

    approveCategorySuggestion: async (eventId: number): Promise<{ success: boolean, data?: any }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/events/approve-category-suggestion/${eventId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            if (!response.ok) {
                throw new Error("Failed to update event status");
            }

            const data = await response.json();
            return {success: data.success, data: data.data};
        } catch (error) {
            console.error("❌ Error setting event status:", error);
            return {success: false};
        }
    },
    changeCategorySuggestion: async (eventId: number, newCategory: string): Promise<{
        success: boolean,
        data?: any
    }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/events/change-category-suggestion/${eventId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({newCategory: newCategory})
            });

            if (!response.ok) {
                throw new Error("Failed to update event status");
            }

            const data = await response.json();
            return {success: data.success, data: data.data};
        } catch (error) {
            console.error("❌ Error setting event status:", error);
            return {success: false};
        }
    },


    setEventStatus: async (eventId: number, status: string): Promise<{
        success: boolean; data?: any; message?: string; code?: string;
        warnings?: Array<{code: string; message: string}>;
    }> => {
        const fallback = "מצב האירוע לא נשמר. נסו שוב או רעננו את הרשימה.";
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/events/set-event-status`, {
                method: "POST",
                headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
                body: JSON.stringify({eventId, status})
            });
            const payload = await response.json().catch(() => null);
            if (!response.ok || payload?.success !== true) {
                throw createApiRequestError(payload, response.status, fallback);
            }
            return {success: true, data: payload.data, warnings: payload.warnings};
        } catch (error) {
            const failure = error as {message?: string; status?: number; code?: string};
            const message = failure?.message && /[א-ת]/.test(failure.message) ? failure.message
                : failure?.status === 401 ? "יש להתחבר מחדש לפני שינוי מצב האירוע."
                    : failure?.status === 403 ? "אין הרשאה לשנות את מצב האירוע הזה."
                        : failure?.status === 404 ? "האירוע לא נמצא. יש לרענן את הרשימה."
                            : fallback;
            return {success: false, message, code: failure?.code};
        }
    },

    getMessageById: async (companyId: string, messageId: string): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/openai/get-message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({companyId, messageId})
            });

            if (!response.ok) {
                throw new Error("Failed to retrieve message by ID");
            }

            const data = await response.json();

            if (data.success && data.data) {
                console.log("DATA: ", data.data);
                return data.data;
            }

            return null;
        } catch (error) {
            console.error("❌ Error in getMessageById API call:", error);
            return null;
        }
    },

    getEvents: async (status: string | null): Promise<CompanyEvent[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/events/get-events`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({status})
            });

            if (!response.ok) {
                throw new Error("Failed to fetch all companies events by user");
            }

            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                return data.data as CompanyEvent[];
            } else {
                return [];
            }
        } catch (error) {
            console.error("❌ Error fetching all companies events by user:", error);
            return [];
        }
    },

    getLatestThinkProcess: async (sessionId: string): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/think-process/get-latest`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({sessionId})
            });

            if (!response.ok) {
                throw new Error("Failed to retrieve latest think process");
            }

            const text = await response.text();
            if (!text) return null;
            try {
                return JSON.parse(text);
            } catch (error) {
                console.error("Error in getLatestThinkProcess (JSON parse):", error);
                return null;
            }
        } catch (error) {
            console.error("Error in getLatestThinkProcess:", error);
            return null;
        }
    },
    getHistoryThinkProcess: async (sessionId: string, signal?: AbortSignal | null): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/think-process/session/${sessionId}/status`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                ...(signal ? {signal: signal} : {}),
            });

            return await response.json();
        } catch (error) {
            console.error("Error in getHistoryThinkProcess:", error);
            return [];
        }
    },

    getThinkProcess: async (processId: string): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/think-process/${processId}/status`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            if (!response.ok) {
                throw new Error("Failed to retrieve think process status");
            }

            const text = await response.text();

            console.log("text: ", text);

            if (!text) return null;

            try {
                return JSON.parse(text);
            } catch (error) {
                console.error("Error in getThinkProcess (JSON parse):", error);
                return null;
            }
        } catch (error) {
            console.error("Error in getThinkProcess:", error);
            return null;
        }
    },

    getFullThinkProcess: async (sessionId: string): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/think-process/get-full`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({sessionId})
            });

            if (!response.ok) {
                throw new Error("Failed to retrieve full think process");
            }

            return await response.json();
        } catch (error) {
            console.error("Error in getFullThinkProcess:", error);
            return null;
        }
    },

    getTranscription: async (meetingId: number, signal?: AbortSignal | null): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/get-transcription`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            ...(signal ? {signal: signal} : {}),
        });
        return await response.json();
    },

    getCalendarWeek: async (startStr: string, signal?: AbortSignal | null): Promise<CalendarEventDto[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/calendar/week?start=${startStr}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            ...(signal ? {signal: signal} : {}),
        });
        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(
                `Failed to fetch calendar week (${response.status}): ${text}`
            );
        }

        const data: CalendarWeekResponse = await response.json();
        return data.events || [];
    },
    approveMeeting: async (meetingId: number): Promise<string> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/approve`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`
            },
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(typeof payload?.error === "string" ? payload.error : "שמירת משימת הפגישה נכשלה");
        return payload;
    },
    getMeetingCalendarOptions: async (companyId: number): Promise<Array<{googleCalendarId: string; label: string; primary: boolean; accessRole: string}>> => {
        if (clientScope !== "workspace") throw new Error("בחירת יומן זמינה רק בסביבת העבודה");
        const response = await fetch(`${meetingsRoot}/company/${encodeURIComponent(companyId)}/calendar-options`, {
            headers: {"Authorization": `Bearer ${getValidToken()}`},
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw createApiRequestError(payload, response.status, "טעינת היומנים נכשלה");
        if (!Array.isArray(payload?.calendars)) throw new Error("טעינת היומנים נכשלה");
        return payload.calendars;
    },
    getMeetingParticipants: async (companyId: number): Promise<MeetingParticipant[]> => {
        if (clientScope !== "workspace") throw new Error("בחירת משתתפים זמינה רק בסביבת העבודה");
        const response = await fetch(`${meetingsRoot}/company/${encodeURIComponent(companyId)}/participants`, {
            headers: {"Authorization": `Bearer ${getValidToken()}`},
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw createApiRequestError(payload, response.status, "טעינת אנשי הקשר נכשלה");
        if (!Array.isArray(payload)) throw new Error("טעינת אנשי הקשר נכשלה");
        return payload;
    },
    getMeetingSummaryUsers: async (meetingId: number): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/get-meeting-summary-users${clientScope === "workspace" ? "?recipient_format=contacts" : ""}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`
            },
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
            throw createApiRequestError(
                payload,
                response.status,
                "לא ניתן לטעון את נמעני סיכום הפגישה"
            );
        }
        return Array.isArray(payload) ? payload : [];
    },
    getMeetingSummaryDeliveryRecipients: async (meetingId: number): Promise<{groups: Array<{groupJid: string; name: string}>; snapshotToken: string}> => {
        if (clientScope !== "workspace") throw new Error("שליחה לקבוצות זמינה בסביבת העבודה");
        const response = await fetch(`${meetingsRoot}/${meetingId}/summary-delivery-recipients`, {
            headers: {"Authorization": `Bearer ${getValidToken()}`},
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw createApiRequestError(payload, response.status, "טעינת קבוצות החברה נכשלה");
        return payload;
    },
    getMonthlyCashflowReportUsers: async (companyId: string): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-monthly-cashflow-report-users/${companyId}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`
            },
        });
        return await response.json();
    },

    getMonthlyCashflowAccounts: async (companyId: number | string): Promise<CashflowWidgetAccount[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-monthly-cashflow-accounts/${companyId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Failed to fetch monthly cashflow accounts. Status: ${response.status}. ${text}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    },

    sendMeetingSummary: async (meetingId: number, users: number[], recipientRows?: Array<{contactId?: number | null}>, delivery?: {groupJids: string[]; snapshotToken: string}): Promise<any> => {
        let recipientPayload: {users: number[]} | {contact_ids: number[]; group_jids?: string[]; delivery_token?: string} = {users};
        if (clientScope === "workspace" && (delivery || recipientRows?.some(row => row.contactId != null))) {
            const allowed = new Set((recipientRows || []).map(row => Number(row.contactId)).filter(id => Number.isSafeInteger(id) && id > 0));
            const selected = Array.from(new Set(users.map(Number)));
            if ((!selected.length && !delivery?.groupJids.length) || selected.some(id => !allowed.has(id))) {
                throw new Error("רשימת הנמענים השתנתה. יש לפתוח אותה מחדש ולבחור למי לשלוח.");
            }
            recipientPayload = {contact_ids: selected, ...(delivery ? {group_jids: delivery.groupJids, delivery_token: delivery.snapshotToken} : {})};
        }
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/send-meeting-summary`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(recipientPayload),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
            throw createApiRequestError(
                payload,
                response.status,
                "שליחת סיכום הפגישה נכשלה"
            );
        }
        return payload;
    },
    getMeetingStatus: async (meetingId: number, signal?: AbortSignal | null): Promise<string> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/get-meeting-status`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`
            },
            ...(signal ? {signal: signal} : {}),
        });
        return await response.text();
    },
    getMeetingSummary: async (meetingId: number, signal?: AbortSignal | null): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/get-summary`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            ...(signal ? {signal: signal} : {}),
        });
        return await response.json();
    },
    searchMeetingSummaries: async (params: {
        query: string;
        companyId?: string | number | null;
        lastMeetingsLimit?: string | number | null;
        limit?: number;
        minSimilarity?: number;
    }): Promise<MeetingSummarySearchResponse> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/summary-search`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params),
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || `Search failed (${response.status})`);
        }

        return data;
    },
    getMeetingSummarySearchCompanies: async (): Promise<MeetingSummarySearchCompany[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/summary-search-companies`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || `Companies loading failed (${response.status})`);
        }

        return data || [];
    },
    searchMeetingFeedback: async (params: {
        query: string;
        companyId?: string | number | null;
        lastMeetingsLimit?: string | number | null;
        limit?: number;
        minSimilarity?: number;
    }): Promise<MeetingFeedbackSearchResponse> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/feedback/search`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params),
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || `Search failed (${response.status})`);
        }

        return data;
    },
    getMeetingFeedbackSearchCompanies: async (): Promise<MeetingFeedbackSearchCompany[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/feedback/search-companies`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || `Companies loading failed (${response.status})`);
        }

        return data || [];
    },
    getKnowledgeBaseCategories: async (): Promise<KnowledgeBaseCategory[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/knowledge-base/categories`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || `Categories loading failed (${response.status})`);
        }

        return data?.categories || [];
    },
    createKnowledgeBaseCategory: async (payload: {
        name: string;
        is_active?: boolean;
    }, requestIntent?: string): Promise<KnowledgeBaseCategory> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/knowledge-base/categories`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => null);
        assertRequestOperationSucceeded(response, data);
        if (!response.ok) {
            throw new Error(data?.error || `Category creation failed (${response.status})`);
        }

        return data.category;
    },
    deleteKnowledgeBaseCategory: async (categoryId: number | string, payload: {edit_token?: string} = {}, requestIntent?: string): Promise<{ id: number }> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/knowledge-base/categories/${categoryId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => null);
        assertRequestOperationSucceeded(response, data);
        if (!response.ok) {
            throw new Error(data?.error || `Category deletion failed (${response.status})`);
        }

        return data;
    },
    getKnowledgeBaseRecords: async (params: {
        query?: string;
        categoryId?: string | number | null;
        activeOnly?: boolean;
        limit?: number;
        offset?: number;
    } = {}): Promise<{ total: number; limit: number; offset: number; records: KnowledgeBaseRecord[] }> => {
        const token = getValidToken();
        const searchParams = new URLSearchParams();

        if (params.query) searchParams.set("query", params.query);
        if (params.categoryId) searchParams.set("categoryId", String(params.categoryId));
        if (params.activeOnly !== undefined) searchParams.set("activeOnly", String(params.activeOnly));
        if (params.limit) searchParams.set("limit", String(params.limit));
        if (params.offset) searchParams.set("offset", String(params.offset));

        const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
        const response = await fetch(`${API_URL}/api/knowledge-base/records${suffix}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || `Records loading failed (${response.status})`);
        }

        return data;
    },
    createKnowledgeBaseRecord: async (payload: {
        title: string;
        content: string;
        category_id?: number | null;
        category?: string;
        tags?: string[];
        is_active?: boolean;
    }, requestIntent?: string): Promise<KnowledgeBaseRecord> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/knowledge-base/records`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => null);
        assertRequestOperationSucceeded(response, data);
        if (!response.ok) {
            throw new Error(data?.error || `Record creation failed (${response.status})`);
        }

        return data.record;
    },
    updateKnowledgeBaseRecord: async (
        recordId: number | string,
        payload: {
            edit_token?: string;
            title?: string;
            content?: string;
            category_id?: number | null;
            category?: string;
            tags?: string[];
            is_active?: boolean;
        },
        requestIntent?: string
    ): Promise<KnowledgeBaseRecord> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/knowledge-base/records/${recordId}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => null);
        assertRequestOperationSucceeded(response, data);
        if (!response.ok) {
            throw new Error(data?.error || `Record update failed (${response.status})`);
        }

        return data.record;
    },
    deleteKnowledgeBaseRecord: async (recordId: number | string, payload: {edit_token?: string} = {}, requestIntent?: string): Promise<{ id: number }> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/knowledge-base/records/${recordId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => null);
        assertRequestOperationSucceeded(response, data);
        if (!response.ok) {
            throw new Error(data?.error || `Record deletion failed (${response.status})`);
        }

        return data;
    },
    searchKnowledgeBase: async (params: {
        query: string;
        categoryId?: string | number | null;
        activeOnly?: boolean;
        limit?: number;
        minSimilarity?: number;
    }): Promise<KnowledgeBaseSearchResponse> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/knowledge-base/search`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params),
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || `Search failed (${response.status})`);
        }

        return data;
    },
    exportKnowledgeBase: async (params: { activeOnly?: boolean } = {}): Promise<KnowledgeBaseExport> => {
        const token = getValidToken();
        const searchParams = new URLSearchParams();
        if (params.activeOnly !== undefined) searchParams.set("activeOnly", String(params.activeOnly));
        const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
        const response = await fetch(`${API_URL}/api/knowledge-base/export${suffix}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || `Export failed (${response.status})`);
        }

        return data;
    },
    importKnowledgeBase: async (file: File): Promise<KnowledgeBaseImportResponse> => {
        const token = getValidToken();
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(`${API_URL}/api/knowledge-base/import`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`
            },
            body: formData,
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || `Import failed (${response.status})`);
        }

        return data;
    },
    meetingPreparation: async (params: any): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/preparation`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params),
        });
        const payload = await response.json().catch(error => {
            if (response.ok) throw error;
            return null;
        });
        if (!response.ok) {
            throw createApiRequestError(payload, response.status, "טעינת ההכנה לפגישה נכשלה");
        }
        return payload;
    },
    getFeedback: async (meetingId: number, signal?: AbortSignal | null): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/feedback/${meetingId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            ...(signal ? {signal: signal} : {}),
        });
        const payload = await response.json().catch(error => {
            if (response.ok) throw error;
            return null;
        });
        if (!response.ok) {
            throw createApiRequestError(payload, response.status, "טעינת המשוב לפגישה נכשלה");
        }
        return payload;
    },
    generateFeedback: async (params: any): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/feedback/generate`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });
        return await response.json();
    },
    getTasks: async (meetingId: number, signal?: AbortSignal | null): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingTasksRoot}/${meetingId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            ...(signal ? {signal: signal} : {}),
        });
        return await response.json();
    },
    getMeetingTaskAssignees: async (meetingId: number, signal?: AbortSignal): Promise<MeetingTaskAssigneeOption[]> => {
        if (clientScope !== 'workspace') return [];
        const token = getValidToken();
        const response = await fetch(`${meetingTasksRoot}/${meetingId}/assignees`, {
            headers: {Authorization: `Bearer ${token}`}, signal,
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(typeof payload?.error === 'string' ? payload.error : 'טעינת רשימת האחראים נכשלה');
        return Array.isArray(payload?.data) ? payload.data : [];
    },
    generateTasks: async (params: any): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingTasksRoot}/generate`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });
        return await response.json();
    },
    approveTask: async (id: number, status: 'approved' | 'completed'): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${meetingTasksRoot}/${id}/approve`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({params: {status}})
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(typeof payload?.error === "string" ? payload.error : "שמירת משימת הפגישה נכשלה");
        return payload;
    },
    updateTask: async (id: number,
                       params: Partial<Pick<MeetingActionItem, 'title' | 'owner' | 'status' | 'assignee_type'>> & {assignee_key?: string}
    ): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${meetingTasksRoot}/${id}/update-task`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({params})
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(typeof payload?.error === "string" ? payload.error : "שמירת משימת הפגישה נכשלה");
        return payload;
    },
    createActionItem: async (id: number,
                             params: any
    ): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${meetingTasksRoot}/${id}/action-items`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(typeof payload?.error === "string" ? payload.error : "שמירת משימת הפגישה נכשלה");
        return clientScope === 'workspace' ? payload?.data ?? payload : payload;
    },

    createRecordingSession: async (meetingId: number, requestId: string): Promise<MeetingRecordingSession> => {
        const response = await fetch(`${recordingSessionsRoot}/${meetingId}/recordings/sessions`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${getValidToken()}`},
            body: JSON.stringify({requestId}),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw createApiRequestError(payload, response.status, 'לא ניתן לפתוח הקלטה. ייתכן שהיא פעילה במכשיר אחר.');
        if (typeof payload.recordingSessionId !== 'string' || typeof payload.recordingCapability !== 'string'
            || !Number.isSafeInteger(payload.nextChunkIndex) || payload.nextChunkIndex < 1
            || !['open', 'sealed', 'completed', 'aborted'].includes(payload.state)) {
            throw new Error('לא התקבל אישור תקין לפתיחת ההקלטה. אפשר לנסות שוב.');
        }
        return payload;
    },
    sealRecordingSession: async (meetingId: number, session: MeetingRecordingSession, uploadIds: string[]): Promise<{noRecording: boolean}> => {
        const response = await fetch(`${recordingSessionsRoot}/${meetingId}/recordings/end`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json', Authorization: `Bearer ${getValidToken()}`,
                'X-Recording-Session': session.recordingSessionId,
                'X-Recording-Capability': session.recordingCapability,
            },
            body: JSON.stringify({uploadIds, pendingChunkIndices: []}),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.success === false || payload.ok === false) {
            throw createApiRequestError(payload, response.status, 'לא ניתן לסיים לפני שכל מקטעי ההקלטה עלו. אפשר לנסות שוב.');
        }
        return {noRecording: payload.noRecording === true};
    },
    startRecording: async (meetingId: number): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/start-recording`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data?.error || `Failed starting recording (${response.status})`);
        }
        return data;
    },
    hasMeetingRecordingChunks: async (
        meetingId: number,
        signal?: AbortSignal | null
    ): Promise<{ hasChunks: boolean; chunksCount: number }> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/recordings/has-chunks`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            ...(signal ? {signal} : {}),
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Failed checking recording chunks (${response.status}): ${text}`);
        }

        const data = await response.json();
        const chunksCount = Number(data?.chunksCount ?? data?.count ?? data?.total ?? 0);
        const hasChunks = Boolean(
            data?.hasChunks ??
            data?.hasAudioChunks ??
            data?.exists ??
            data?.recordingStarted ??
            chunksCount > 0
        );

        return {
            hasChunks,
            chunksCount: Number.isFinite(chunksCount) ? chunksCount : 0,
        };
    },
    getMeetingRecordingPlayback: async (
        meetingId: number,
        signal?: AbortSignal | null
    ): Promise<MeetingRecordingPlayback> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/recordings/playback`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            ...(signal ? {signal} : {}),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.success === false) {
            throw createApiRequestError(
                payload,
                response.status,
                "טעינת הקלטת הפגישה נכשלה"
            );
        }

        const source = payload?.data ?? payload ?? {};
        const tracks = (Array.isArray(source.tracks) ? source.tracks : [])
            .map((track: any, position: number): MeetingRecordingTrack | null => {
                const url = String(track?.url || "").trim();
                if (!url) return null;
                const index = Number(track?.index);
                return {
                    index: Number.isFinite(index) ? index : position + 1,
                    url,
                    contentType: String(track?.contentType || "").trim() || null,
                };
            })
            .filter((track: MeetingRecordingTrack | null): track is MeetingRecordingTrack => Boolean(track))
            .sort((left: MeetingRecordingTrack, right: MeetingRecordingTrack) => left.index - right.index);

        if (!tracks.length) {
            const fallbackUrl = String(source.url || "").trim();
            if (fallbackUrl) {
                tracks.push({index: 1, url: fallbackUrl, contentType: null});
            }
        }
        if (!tracks.length) {
            throw new Error("הקלטת הפגישה אינה זמינה כרגע");
        }

        return {
            tracks,
            expiresInSeconds: Number(source.expiresInSeconds) || 0,
        };
    },
    endMeeting: async (meetingId: number): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/end`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            /* #981: הקוד נשמר כדי שהמסך ידע ש-NO_RECORDING_CHUNKS אינו תקלה שחולפת בלחיצה נוספת */
            throw Object.assign(
                new Error(data?.error || data?.message || `Failed ending meeting (${response.status})`),
                {code: typeof data?.code === 'string' ? data.code : undefined, status: response.status},
            );
        }
        return data;
    },
    createMeeting: async (params: any): Promise<Meeting | null> => {
        const {participant_ids, ...legacyParams} = params;
        const payload = clientScope === "workspace"
            ? {...params, participant_ids: Array.isArray(participant_ids) ? participant_ids : []}
            : legacyParams;
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/create`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });
        // A rejected create used to be parsed as if it were a meeting, so the
        // caller reported "no meeting id" instead of the reason the server gave.
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw createApiRequestError(data, response.status, "לא ניתן לפתוח פגישה");
        }
        return data;
    },
    updateMeeting: async (meetingId: number, params: any): Promise<any[]> => {
        const payload = {...params};
        if (clientScope !== "workspace") delete payload.participant_ids;
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });
        return await response.json();
    },
    inactiveCompany: async (id: number): Promise<any> => {
        const token = getValidToken();
        return await fetch(`${API_URL}/api/companies/${id}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
    },
    deleteMeeting: async (meetingId: number): Promise<any> => {
        const token = getValidToken();
        return await fetch(`${meetingsRoot}/${meetingId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
    },
    updateMeetingSummary: async (meetingId: number, params: any): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/update-summary`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                params
            })
        });
        return await response.json();
    },
    updateMeetingTopicSummaryRow: async (meetingId: number, params: any): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/update-topic-summary-row`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                params
            })
        });
        return await response.json();
    },

    deleteMeetingSummary: async (meetingId: number): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/delete-summary`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        return await response.json();
    },
    deleteTopicSummaryRow: async (meetingId: number, params: any): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/delete-topic-summary-row`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                params
            })
        });
        return await response.json();
    },

    getCompanyMeetings: async (companyId: string): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/company/${companyId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json();
        return data;
    },
    getAllMeetingsByIds: async (companyIds: any): Promise<any[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${meetingsRoot}/company/getAllMeetingsByIds`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({companyIds})
            });

            if (!response.ok) {
                throw new Error("Failed to getAllMeetingsByIds");
            }

            return await response.json();
        } catch (error) {
            console.error("❌ Error getAllMeetingsByIds status:", error);
            return [];
        }
    },
    uploadMeetingAudioChunk: async (meetingId: string, audioBlob: Blob | File | null | undefined, fileName: string, chunkMetadata: {
        index: number;
        uploadId?: string;
    }, recordingSession?: MeetingRecordingSession | null): Promise<{ success: boolean; message?: string }> => {
        try {
            if (!audioBlob) {
                return {success: false, message: "audioBlob is required"};
            }
            const token = getValidToken();
            const formData = new FormData();
            formData.append('audio', audioBlob, fileName);
            formData.append('chunk_metadata', JSON.stringify(chunkMetadata));
            const response = await fetch(`${recordingSession ? recordingSessionsRoot : meetingsRoot}/${meetingId}/recordings/chunk`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...(recordingSession ? {
                        'X-Recording-Session': recordingSession.recordingSessionId,
                        'X-Recording-Capability': recordingSession.recordingCapability,
                    } : {}),
                },
                body: formData
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return {success: true};
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("❌ Error uploading audio chunk:", errorMessage);
            return {success: false, message: errorMessage};
        }
    },
    recordMeetingDiagnostic: async (
        meetingId: number,
        payload: Record<string, unknown>
    ): Promise<void> => {
        const token = getValidToken();
        const response = await fetch(`${meetingsRoot}/${meetingId}/recordings/diagnostic`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            throw new Error(`Failed recording diagnostic (${response.status})`);
        }
    },
    uploadMeetingTranscript: async (
        meetingId: string,
        files: File | File[]
    ): Promise<{ success: boolean; message?: string }> => {
        try {
            const token = getValidToken();

            const form = new FormData();
            const uploadFiles = Array.isArray(files) ? files : [files];
            uploadFiles.forEach((file) => form.append('files', file));

            const res = await fetch(
                `${meetingsRoot}/${meetingId}/upload-transcript`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    body: form
                }
            );

            if (!res.ok) {
                const message = await res.text().catch(() => '');
                throw new Error(message || `Upload transcript failed ${res.status}`);
            }

            return { success: true };
        } catch (err: any) {
            return { success: false, message: err?.message };
        }
    },
    uploadMeetingAudioViaSignedUrl: async (
        meetingId: string,
        file: File,
        chunkMetadata: { index: number }
    ): Promise<{ success: boolean; message?: string }> => {
        try {
            const token = getValidToken();

            const res = await fetch(
                `${meetingsRoot}/${meetingId}/upload-url`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        index: chunkMetadata.index,
                        originalFilename: file.name,
                        mimetype: file.type
                    })
                }
            );

            if (!res.ok) {
                throw new Error(`Failed getting upload url ${res.status}`);
            }

            const { uploadUrl, audioObject } = await res.json();

            const putRes = await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': file.type
                },
                body: file
            });

            if (!putRes.ok) {
                throw new Error(`Upload to GCS failed ${putRes.status}`);
            }

            const confirmRes = await fetch(
                `${meetingsRoot}/${meetingId}/chunk-uploaded`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        index: chunkMetadata.index,
                        audioObject
                    })
                }
            );

            if (!confirmRes.ok) {
                throw new Error(`Confirm upload failed ${confirmRes.status}`);
            }

            return { success: true };
        } catch (err: any) {
            return { success: false, message: err?.message };
        }
    },
    createCompanyWithUser: async (params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/create-company-with-user`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });
        const data = await response.json().catch(() => null) as any;
        if (!response.ok || data?.success === false) {
            throw createApiRequestError(
                data,
                response.status,
                `Failed to create company with user (${response.status})`
            );
        }
        return data;
    },
    getCompanyDetails: async (companyId: string): Promise<GetCompanyDetailsResponse> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/${shouldUseUserAccountsExperience(decodeAuthTokenClaims(token)) ? "user-accounts/company-contacts/company-details" : "companies/get-company-details"}/${companyId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw createApiRequestError(
                data,
                response.status,
                `Failed to fetch company details (${response.status})`
            );
        }
        return data as GetCompanyDetailsResponse;
    },
    /** JAMPA: fetch company data for edit modal. Replace path when endpoint is provided. */
    getEditJampaCompanyDetails: async (companyId: string | number): Promise<{
        company_name: string;
        mainCompanyUser: {
            id: string;
            first_name: string;
            last_name: string;
            email: string | null;
            phone_number: string;
        } | null;
    }> => {
        const token = getValidToken();
        // TODO: replace with actual endpoint when provided
        const response = await fetch(`${API_URL}/api/companies/get-jampa-company-details/${companyId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });
        if (!response.ok) {
            throw new Error(`Failed to load edit data. Status: ${response.status}`);
        }
        return await response.json();
    },
    /** JAMPA: save edited company data. Replace path when endpoint is provided. */
    updateEditJampaCompany: async (
        companyId: string | number,
        payload: {
            mainCompanyUser: {
                id: string;
                first_name: string;
                last_name: string;
                phone_number: string;
            };
        }
    ): Promise<{ success?: boolean; message?: string; [key: string]: unknown }> => {
        const token = getValidToken();
        // TODO: replace with actual endpoint when provided
        const response = await fetch(`${API_URL}/api/companies/edit-jampa-company-details/${companyId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => null) as any;
        if (!response.ok || data?.success === false) {
            throw createApiRequestError(
                data,
                response.status,
                `Failed to save. Status: ${response.status}`
            );
        }
        return data;
    },
    restoreCompany: async (companyId: string | number): Promise<{success: boolean; updatedCompany?: Company}> => {
        const response = await fetch(`${API_URL}/api/companies/restore-company`, {
            method: "POST",
            headers: {"Content-Type": "application/json", "Authorization": `Bearer ${getValidToken()}`},
            body: JSON.stringify({id: Number(companyId)}),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || data?.success !== true) {
            throw new Error(data?.message || `לא ניתן לשחזר את החברה (${response.status})`);
        }
        return data;
    },
    deleteCompanyAndUsers: async (
        companyId: string | number
    ): Promise<{ success: boolean; message?: string; [key: string]: unknown }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies/delete-company-and-users/${companyId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                return { success: false, message: data?.message || `Failed to delete company. Status: ${response.status}` };
            }

            if (data?.success === false) {
                return { success: false, message: data?.message || "Failed to delete company" };
            }

            return { success: true, message: data?.message || "Company deleted successfully" };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            console.error("Error deleting company and users:", errorMessage);
            return { success: false, message: errorMessage };
        }
    },
    getWorkspaceBudgetReport: async (companyId: number): Promise<{status: "published"; publishedAt: string; categories: any[]}> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/workspace/companies/${companyId}/reports/budget`, {
            method: "GET", headers: {"Authorization": `Bearer ${token}`}
        });
        const data = await response.json();
        if (!response.ok) throw createApiRequestError(data, response.status, 'לא ניתן לטעון את דוח התקציב.');
        return data;
    },
    downloadWorkspaceBudgetReport: async (companyId: number): Promise<void> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/workspace/companies/${companyId}/reports/budget/excel`, {
            method: "GET", headers: {"Authorization": `Bearer ${token}`}
        });
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw createApiRequestError(data, response.status, 'לא ניתן לייצא את דוח התקציב.');
        }
        const url = window.URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = url; link.download = `budget-report-${companyId}.xlsx`;
        document.body.appendChild(link); link.click(); link.remove();
        window.URL.revokeObjectURL(url);
    },
    createBudgetReport: async (companyId: number): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/create-budget-report/${companyId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json();
        if (!response.ok) {
            throw Object.assign(new Error(data?.message || 'לא ניתן לטעון את דוח התקציב.'),
                {status: response.status, code: data?.code});
        }
        return data;
    },
    createBudgetReportExcel: async (companyId: string): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/create-budget-report-excel/${companyId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to download budget report. Status ${response.status}`);
        }
        const formatDateForFile = (d: Date): string => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `budget-report-${companyId}-${formatDateForFile(new Date())}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    },
    updateCompanyDetails: async (params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/update-company-details`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });
        const data = await response.json().catch(() => null) as any;
        if (!response.ok || data?.success === false) {
            throw createApiRequestError(
                data,
                response.status,
                `Failed to update company details (${response.status})`
            );
        }
        return data;
    },
    getFinanceData: async (companyId: string): Promise<GetFinanceDataResponse> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-finance-data/${companyId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json();
        return data;
    },
    getMonthlyCashflowData: async (companyId: number, params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-monthly-cashflow-data/${companyId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });
        const data = await response.json();
        if (!response.ok) throw createApiRequestError(data, response.status, 'לא ניתן לטעון את דוח התזרים');
        return data;
    },
    getFutureMonthlyCashflowTargets: async (companyId: number, params: any): Promise<any> => {
        const token = getValidToken();
        const operation = params?.forceRefresh === true || params?.refreshRequested === true || params?.saveSearchRange === true
            ? 'prepare-future-monthly-cashflow-targets' : 'get-future-monthly-cashflow-targets';
        const response = await fetch(`${API_URL}/api/companies/${operation}/${companyId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw Object.assign(createApiRequestError(data, response.status, "Failed to fetch future monthly cashflow targets"), {canRefreshReport: data?.canRefreshReport === true});
        }

        return await response.json();
    },
    getFutureCashflowGapClassificationTypes: async (): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/future-cashflow-gap-classification-types`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.message || "Failed to fetch future cashflow gap classification types");
        }

        return await response.json();
    },
    updateFutureCashflowGapClassification: async (companyId: number, params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/future-cashflow-gap-classification/${companyId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.message || "Failed to update future cashflow gap classification");
        }

        return await response.json();
    },
    clearFutureCashflowGapClassification: async (companyId: number, params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/future-cashflow-gap-classification/${companyId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.message || "Failed to clear future cashflow gap classification");
        }

        return await response.json();
    },
    updateFutureMonthlyCashflowTarget: async (companyId: number, params: any, requestIntent?: string): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/future-monthly-cashflow-target/${companyId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw createApiRequestError(data, response.status, "Failed to update future monthly cashflow target");
        }

        const body = await response.json();
        assertRequestOperationSucceeded(response, body);
        return body;
    },
    lockFutureMonthlyCashflowTargets: async (companyId: number, params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/future-monthly-cashflow-targets-lock/${companyId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.message || "Failed to lock future monthly cashflow targets");
        }

        return await response.json();
    },
    updateFutureCashflowStartBalance: async (companyId: number, futureCashflowStartBalance: number): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/future-cashflow-start-balance/${companyId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({futureCashflowStartBalance})
        });

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.message || "Failed to update future cashflow start balance");
        }

        return await response.json();
    },
    updateFutureCashflowExpenseClassification: async (companyId: number, params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/future-cashflow-expense-classification/${companyId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.message || "Failed to update future cashflow expense classification");
        }

        return await response.json();
    },
    getFutureCashflowExpenseClassificationRecommendations: async (companyId: number, params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/future-cashflow-expense-classification-recommendations/${companyId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.message || "Failed to load future cashflow expense classification recommendations");
        }

        return await response.json();
    },
    downloadFutureCashflowReportExcel: async (companyId: number, params: any): Promise<void> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/future-cashflow-report-excel/${companyId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Failed to download future cashflow report. Status ${response.status}. ${text}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const formatDateForFile = (d: Date): string => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };
        const link = document.createElement('a');

        link.href = url;
        link.download = `future-cashflow-report-${companyId}-${formatDateForFile(new Date())}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    },
    getMultiCompanyMonthlyCashflowData: async (params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-monthly-cashflow-data-companies`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Failed to fetch multi-company monthly cashflow report. Status ${response.status}. ${text}`);
        }

        return await response.json();
    },
    getMultiCompanyMonthlyCashflowDataExcel: async (params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-monthly-cashflow-data-companies-excel`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Failed to download multi-company monthly cashflow report. Status ${response.status}. ${text}`);
        }

        const formatDateForFile = (d: Date): string => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `multi-company-monthly-cashflow-report-${formatDateForFile(new Date())}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    },
    sendWhatsappFreeTextMessageToUser: async (
        userId: any,
        eventId: any,
        message: string,
        contextId?: any,
        templateName?: string | null,
        phoneNumber?: string | null
    ): Promise<any> => {
        const token = getValidToken();
        const payload: any = { userId, eventId, messageContent: message, contextId };
        if (templateName) {
            payload.templateName = templateName;
        }
        if (phoneNumber) {
            payload.phoneNumber = phoneNumber;
        }
        const response = await fetch(`${API_URL}/api/whatsapp/send-free-text-message`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });
        return await response.json();
    },
    downloadWhatsappFile: async (fileId: string): Promise<void> => {
        try {
            const token = getValidToken();
            if (!token) {
                throw new Error("No authentication token");
            }
            const response = await fetch(`${API_URL}/api/whatsapp/download-whatsapp-file/${fileId}`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error(`Failed to download file. Status: ${response.status}`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            // Try to extract filename from Content-Disposition header
            const contentDisposition = response.headers.get("Content-Disposition");
            let filename = `whatsapp-file-${fileId}`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading WhatsApp file:", error);
            throw error;
        }
    },
    /** Returns an object URL for streaming playback (e.g. audio). Caller must revoke the URL when done. */
    getWhatsappFileBlobUrl: async (fileId: string): Promise<string> => {
        const token = getValidToken();
        if (!token) {
            throw new Error("No authentication token");
        }
        const response = await fetch(`${API_URL}/api/whatsapp/download-whatsapp-file/${fileId}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`
            }
        });
        if (!response.ok) {
            throw new Error(`Failed to load file. Status: ${response.status}`);
        }
        const blob = await response.blob();
        return window.URL.createObjectURL(await prepareFileBlob(blob, "", fileId));
    },
    getMonthlyCashflowDataExcel: async (companyId: number, params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-monthly-cashflow-data-excel/${companyId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });
        if (!response.ok) {
            throw new Error(`Failed to download monthly cashflow report. Status ${response.status}`);
        }
        const formatDateForFile = (d: Date): string => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `monthly-cashflow-report-${companyId}-${formatDateForFile(new Date())}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    },
    editFinanceData: async (params: any): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/edit-finance-data`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw createApiRequestError(data, response.status, "שגיאה בעדכון נתוני פיננסים");
        }
        return data;
    },
    getBillingCollectionsDashboard: async (params: { from?: string; to?: string } = {}): Promise<BillingCollectionsDashboard> => {
        const token = getValidToken();
        const search = new URLSearchParams();
        if (params.from) search.set("from", params.from);
        if (params.to) search.set("to", params.to);
        const suffix = search.toString() ? `?${search.toString()}` : "";
        const response = await fetch(`${API_URL}/api/billing-collections/dashboard${suffix}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בטעינת דשבורד תקבולים");
        }
        return data;
    },
    getTransferBillingAccounts: async (): Promise<Array<{id: number; name: string; payer_name: string | null}>> => {
        const response = await fetch(`${API_URL}/api/billing-collections/billing-accounts`, {
            headers: {Authorization: `Bearer ${getValidToken()}`},
        });
        const data = await response.json();
        if (!response.ok) throw createApiRequestError(data, response.status, 'לא ניתן לטעון חשבונות העברה');
        return data;
    },
    getBillingRecurringSales: async (): Promise<BillingRecurringSale[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/recurring-sales`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בטעינת הוראות קבע");
        }
        return Array.isArray(data) ? data : [];
    },
    refreshBillingRecurringSales: async (): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/refresh-recurring-sales`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בסנכרון הוראות קבע");
        }
        return data;
    },
    getBillingSaleHistory: async (recurringSaleId: string): Promise<BillingSaleHistory> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/sale-history/${encodeURIComponent(recurringSaleId)}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בטעינת היסטוריית חיובים");
        }
        return data;
    },
    createBillingReceipt: async (params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/create-receipt`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(params),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בהפקת חשבונית");
        }
        return data;
    },
    addBillingAccount: async (params: {
        type: "TRANSFER" | "IMMEDIATE";
        name: string;
        payer_name?: string;
        default_amount?: number | null;
        notes?: string;
        charge_kind?: "DIRECT" | "VARIABLE";
        card?: BillingCardInput;
        invoice_email?: string;
        invoice_desc?: string;
    }): Promise<BillingAccount> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/billing-accounts`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(params),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה ביצירת חשבון העברה");
        }
        return data;
    },
    saveBillingAccountCard: async (billingAccountId: number, params: { card: BillingCardInput; email?: string; description?: string }): Promise<BillingAccount> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/billing-accounts/${encodeURIComponent(String(billingAccountId))}/card`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(params),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בשמירת כרטיס");
        }
        return data;
    },
    assignBillingCompanyAccount: async (params: {
        companyId: number;
        type: "recurring" | "account" | "transfer" | "immediate";
        subscriptionId?: string | null;
        billingAccountId?: number | null;
        nextBillingDate?: string | null;
    }): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/assign-account`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(params),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בשיוך חשבון משלם");
        }
        return data;
    },
    chargeBillingImmediateAccount: async (params: {
        billingAccountId: number;
        companyId?: number | null;
        amount: number;
        description?: string;
        email?: string;
        month?: string;
    }): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/charge-immediate`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(params),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בחיוב מיידי");
        }
        return data;
    },
    addBillingIgnoredAccount: async (params: { match?: string; customer_id?: number | null; reason?: string }): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/ignored-accounts`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(params),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בהוספת חשבון מוחרג");
        }
        return Array.isArray(data) ? data : [];
    },
    removeBillingIgnoredAccount: async (id: number): Promise<any[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/ignored-accounts/${id}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בהסרת חשבון מוחרג");
        }
        return Array.isArray(data) ? data : [];
    },
    addBillingPaymentPage: async (params: { token: string; name?: string; group_id?: string }): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/billing-collections/payment-pages`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(params),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || "שגיאה בהוספת דף תשלום");
        }
        return data;
    },
    createTaskEvent: async (params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/events/create-task-event`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(params)
        });
        /* אייל 23.09: שגיאת שרת חייבת להגיע למשתמש — לא לסגור דיאלוג כאילו נשמר */
        const data = await response.json().catch(() => null);
        if (!response.ok || data?.success === false) throw new Error(data?.message || data?.error || "שמירת המשימה נכשלה");
        return data;
    },
    updateTaskEvent: async (id: number, params: any): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/events/${id}/update-task-event`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({params})
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || data?.success === false) throw new Error(data?.message || data?.error || "עדכון המשימה נכשל");
        return data;
    },
    deleteJampaStrengtheningEvent: async (id: number): Promise<{ success: boolean; data?: CompanyEvent | null }> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/events/${id}/jampa-strengthening`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        return await response.json();
    },
    updateJampaStrengtheningEvent: async (
        id: number,
        eventDescription: string
    ): Promise<{ success: boolean; data?: CompanyEvent | null }> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/events/${id}/jampa-strengthening`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({event_description: eventDescription})
        });
        return await response.json();
    },
    replaceJampaStrengtheningEvent: async (id: number): Promise<{ success: boolean; data?: CompanyEvent | null }> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/events/${id}/jampa-strengthening/replace`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        return await response.json();
    },
    refreshSummary: async (): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/refresh-summary`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json();
        return data;
    },
    getCategoriesWithDiff: async (): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies/get-categories-with-diff`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch categories with diff. Status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching categories with diff:", error);
            throw error;
        }
    },
    getBudgetSettings: async (): Promise<{ DIFF_VS_BUDGET?: number; BUDGET_VS_PREV_MONTH?: number }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/admin-settings/budget-settings`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch budget settings. Status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching budget settings:", error);
            throw error;
        }
    },
    setBudgetSettings: async (diffVsBudget: number, budgetVsPrevMonth: number): Promise<{ success: boolean }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/admin-settings/budget-settings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    DIFF_VS_BUDGET: diffVsBudget,
                    BUDGET_VS_PREV_MONTH: budgetVsPrevMonth
                })
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
                throw new Error(errorData.error || `Failed to save budget settings. Status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error saving budget settings:", error);
            throw error;
        }
    },
    refreshBudgetReport: async (companyId: string | number): Promise<{ success: boolean }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies/refresh-budget-report/${companyId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error(`Failed to refresh budget report. Status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error refreshing budget report:", error);
            throw error;
        }
    },
    snoozeBudgetCategory: async (budgetCategory: any): Promise<{ success: boolean }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/companies/snooze-budget-category`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    budgetCategory: budgetCategory
                })
            });
            if (!response.ok) {
                throw new Error(`Failed to snooze budget category. Status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error snoozing budget category:", error);
            throw error;
        }
    },
    updateTargetDefinition: async (id:number, payload:any) => {
        const token = getValidToken();
        return fetch(`${scopedApiRoot}targets/definition/${id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });
    },
    previewMetricGoogleSheet: async (companyId: string | number, payload: any): Promise<any> => {
        const response = await fetch(`${scopedApiRoot}companies/${companyId}/metrics/sheet-preview`, {
            method: "POST", headers: {"Content-Type": "application/json", "Authorization": `Bearer ${getValidToken()}`},
            body: JSON.stringify(payload),
        });
        const body = await response.json().catch(() => null);
        if (!response.ok) throw new Error(body?.message || "קריאת הגיליון נכשלה");
        return body.data;
    },
    createCompanyMetric: async (companyId: string | number, payload: any, requestIntent?: string): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${scopedApiRoot}companies/${companyId}/metrics`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => null);
        assertRequestOperationSucceeded(response, data);
        if (!response.ok) throw new Error(data?.message || "Failed to create metric");
        return data?.data ?? data;
    },
    updateMetric: async (metricId: number, payload: any, requestIntent?: string): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${scopedApiRoot}metrics/${metricId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw createApiRequestError(data, response.status, "Failed to update metric");
        assertRequestOperationSucceeded(response, data);
        return data?.data ?? data;
    },
    setMetricMonth: async (
        metricId: number,
        month: string,
        payload: {target?: number | null; actual?: number; edit_token?: string; definition_edit_token?: string},
        requestIntent?: string
    ): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${scopedApiRoot}metrics/${metricId}/months/${encodeURIComponent(month)}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw createApiRequestError(data, response.status, "Failed to update metric month");
        assertRequestOperationSucceeded(response, data);
        return data?.data ?? data;
    },
    askMetricClient: async (metricId: number): Promise<any> => {
        const token = getValidToken();
        const response = await fetch(`${scopedApiRoot}metrics/${metricId}/ask`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new Error(data?.message || "Failed to request metric value");
        return data?.data ?? data;
    },
    getMonthlyTargets: async (
        companyId: number | string,
        options?: { sync?: boolean | "background"; fresh?: boolean }
    ): Promise<any> => {
        const search = new URLSearchParams({
            company_id: String(companyId),
        });

        // Old widgets supplied sync flags on mount. Those remain pure reads;
        // only an explicit fresh source refresh invokes a mutation command.
        if (options?.fresh === true && options.sync) {
            return refreshMonthlyTargets(companyId, options.sync);
        }

        const loadMonthlyTargets = async () => {
            const token = getValidToken();

            const response = await fetch(
                `${scopedApiRoot}monthly-targets?${search.toString()}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) throw new Error("לא ניתן לטעון כרגע את המדדים");
            return await response.json();
        };

        if (clientScope === "legacy") {
            return !options?.sync
                ? loadMonthlyTargets()
                : dedupeInFlight(`monthly-targets:${search.toString()}:${getValidToken()}`, loadMonthlyTargets);
        }
        // A workspace read after a mutation must not reuse an older request.
        return options?.fresh
            ? loadMonthlyTargets()
            : dedupeInFlight(`workspace-monthly-targets:${search.toString()}:${getValidToken()}`, loadMonthlyTargets);
    },

    updateWorkingDays: async (monthId: number, body: any): Promise<void> => {
        monthId = await resolveStoredMonthId(monthId, body.company_id ?? body.companyId);
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/company-months/${monthId}/working-days`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(body)
        });
        if (!response.ok) throw new Error("Failed to update working days");
    },

    getAgentLogs: async (processId: string): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/agents-logs/get-logs/${processId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to fetch agent logs (${response.status}): ${text}`);
            }

            return await response.json();
        } catch (error) {
            console.error("❌ Error fetching agent logs:", error);
            throw error;
        }
    },

    getThinkingProcessByUserQuestionId: async (userQuestionId: string): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(
                `${API_URL}/api/think-process/get-thinking-process/${userQuestionId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(
                    `Failed to fetch thinking process (${response.status}): ${text}`
                );
            }

            return await response.json();
        } catch (error) {
            console.error("❌ Error fetching thinking process:", error);
            throw error;
        }
    },

    getSynthesizedMessages: async (): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/synthesized-messages/get-synthesized-messages`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to fetch synthesized messages (${response.status}): ${text}`);
            }
            return await response.json();
        } catch (error) {
            console.error("❌ Error fetching synthesized messages:", error);
            throw error;
        }
    },

    createTarget: async (body: any, requestIntent?: string): Promise<any> => {
        body = {...body, company_month_id: await resolveStoredMonthId(Number(body.company_month_id), body.company_id ?? body.companyId)};
        const token = getValidToken();
        const response = await fetch(`${scopedApiRoot}targets`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(body)
        });
        assertRequestOperationSucceeded(response, await response.clone().json().catch(() => null));
        if (!response.ok) throw new Error("Failed to create target");
        return await response.json();
    },

    updateTarget: async (id: number, body: any, requestIntent?: string): Promise<void> => {
        if (body.company_month_id != null) body = {...body,
            company_month_id: await resolveStoredMonthId(Number(body.company_month_id), body.company_id ?? body.companyId)};
        const token = getValidToken();
        const response = await fetch(`${scopedApiRoot}targets/${id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(body)
        });
        assertRequestOperationSucceeded(response, await response.clone().json().catch(() => null));
        if (!response.ok) throw new Error("Failed to update target");
    },

    deleteTarget: async (id: number, body: any): Promise<void> => {
        const token = getValidToken();
        const response = await fetch(`${scopedApiRoot}targets/${id}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(body)
        });
        if (!response.ok) throw new Error("Failed to delete target");
    },

    deleteTargetDefinition: async (id: number, body: any, requestIntent?: string): Promise<void> => {
        const token = getValidToken();
        const response = await fetch(`${scopedApiRoot}targets/definition/${id}/delete`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                ...(requestIntent ? {"Idempotency-Key": requestIntent} : {})
            },
            body: JSON.stringify(body)
        });
        assertRequestOperationSucceeded(response, await response.clone().json().catch(() => null));
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.message || "Failed to delete target definition");
        }
    },

    getAgentChatHistory: async (processId: string): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/agents-logs/get-chat-history/${processId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to fetch agent chat history (${response.status}): ${text}`);
            }

            return await response.json();
        } catch (error) {
            console.error("❌ Error fetching agent chat history:", error);
            return [];
        }
    },

    analyzeAgentLogs: async (processId: string, prompt: string): Promise<any> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/agents-logs/analyze-logs/${processId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ prompt }),
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to analyze agent logs (${response.status}): ${text}`);
            }

            const text = await response.text();
            try {
                return JSON.parse(text);
            } catch {
                return text;
            }
        } catch (error) {
            console.error("❌ Error analyzing agent logs:", error);
            throw error;
        }
    },





    getBusinessProcessPrograms: async (businessId: number | string): Promise<BusinessProcessProgram[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-programs/${businessId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch business process programs. Status: ${response.status}`);
            }

            const data = await response.json();
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error("Error fetching business process programs:", error);
            return [];
        }
    },

    getBusinessProcessProgramsForSettings: async (businessId: number | string): Promise<BusinessProcessProgram[]> => {
        const token = getValidToken();
        // Identity, rather than the selected UI mode, chooses the management
        // boundary. Legacy settings keep their original service contract.
        if (!isUserAccountsSession(decodeAuthTokenClaims(token))) {
            return api.getBusinessProcessPrograms(businessId);
        }
        const response = await fetch(`${API_URL}/api/workspace/business-process-programs/${businessId}`, {
            method: "GET",
            headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch business process settings. Status: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    },

    createBusinessProcessProgram: async (
        payload: CreateBusinessProcessProgramPayload
    ): Promise<BusinessProcessProgram | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-programs`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to create business process program. Status: ${response.status}. ${text}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error creating business process program:", error);
            return null;
        }
    },

    updateBusinessProcessProgram: async (
        id: number | string,
        payload: UpdateBusinessProcessProgramPayload
    ): Promise<BusinessProcessProgram | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-programs/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw Object.assign(new Error(`Failed to update business process program. Status: ${response.status}. ${text}`), {status: response.status});
            }

            return await response.json();
        } catch (error) {
            if (payload.edit_token != null) throw error;
            console.error("Error updating business process program:", error);
            return null;
        }
    },

    deleteBusinessProcessProgram: async (
        id: number | string,
        options: {edit_token?: string} = {}
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-programs/${id}`, {
                method: "DELETE",
                ...(options.edit_token == null ? {} : {body: JSON.stringify(options)}),
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (options.edit_token != null) throw Object.assign(new Error(data?.error || "לא ניתן לשמור את התהליך"), {status: response.status, code: data?.code});
                return { success: false, error: data?.error || `Failed to delete process program. Status: ${response.status}` };
            }

            return { success: Boolean(data?.success) };
        } catch (error) {
            if (options.edit_token != null) throw error;
            console.error("Error deleting business process program:", error);
            return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
        }
    },

    getBusinessProcessStageTemplates: async (businessId: number | string): Promise<BusinessProcessStageTemplate[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-stage-templates/${businessId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch business process stage templates. Status: ${response.status}`);
            }

            const data = await response.json();
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error("Error fetching business process stage templates:", error);
            return [];
        }
    },

    createBusinessProcessStageTemplate: async (
        payload: CreateBusinessProcessStagePayload
    ): Promise<BusinessProcessStageTemplate | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-stage-templates`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to create business process stage template. Status: ${response.status}. ${text}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error creating business process stage template:", error);
            return null;
        }
    },

    updateBusinessProcessStageTemplate: async (
        id: number | string,
        payload: UpdateBusinessProcessStagePayload
    ): Promise<BusinessProcessStageTemplate | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-stage-templates/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw Object.assign(new Error(`Failed to update business process stage template. Status: ${response.status}. ${text}`), {status: response.status});
            }

            return await response.json();
        } catch (error) {
            if (payload.edit_token != null) throw error;
            console.error("Error updating business process stage template:", error);
            return null;
        }
    },

    deleteBusinessProcessStageTemplate: async (
        id: number | string,
        options: {edit_token?: string} = {}
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-stage-templates/${id}`, {
                method: "DELETE",
                ...(options.edit_token == null ? {} : {body: JSON.stringify(options)}),
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (options.edit_token != null) throw Object.assign(new Error(data?.error || "לא ניתן לשמור את התהליך"), {status: response.status, code: data?.code});
                return { success: false, error: data?.error || `Failed to delete stage template. Status: ${response.status}` };
            }

            return { success: Boolean(data?.success) };
        } catch (error) {
            if (options.edit_token != null) throw error;
            console.error("Error deleting business process stage template:", error);
            return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
        }
    },

    addBusinessProcessStageTemplateLine: async (
        stageTemplateId: number | string,
        payload: AddBusinessProcessStageLinePayload
    ): Promise<BusinessProcessStageTemplateLine | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-stage-templates/${stageTemplateId}/lines`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to add stage template line. Status: ${response.status}. ${text}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error adding business process stage template line:", error);
            return null;
        }
    },

    updateBusinessProcessStageTemplateLine: async (
        id: number | string,
        payload: UpdateBusinessProcessStageLinePayload
    ): Promise<BusinessProcessStageTemplateLine | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-stage-templates/lines/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw Object.assign(new Error(`Failed to update stage template line. Status: ${response.status}. ${text}`), {status: response.status});
            }

            return await response.json();
        } catch (error) {
            if (payload.edit_token != null) throw error;
            console.error("Error updating business process stage template line:", error);
            return null;
        }
    },

    deleteBusinessProcessStageTemplateLine: async (
        id: number | string,
        options: {edit_token?: string} = {}
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-stage-templates/lines/${id}`, {
                method: "DELETE",
                ...(options.edit_token == null ? {} : {body: JSON.stringify(options)}),
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (options.edit_token != null) throw Object.assign(new Error(data?.error || "לא ניתן לשמור את התהליך"), {status: response.status, code: data?.code});
                return { success: false, error: data?.error || `Failed to delete stage template line. Status: ${response.status}` };
            }

            return { success: Boolean(data?.success) };
        } catch (error) {
            if (options.edit_token != null) throw error;
            console.error("Error deleting business process stage template line:", error);
            return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
        }
    },

    reorderBusinessProcessStageTemplates: async (
        businessId: number | string,
        payload: ReorderBusinessProcessStagesPayload
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-stage-templates/${businessId}/reorder`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (payload.edit_tokens != null) throw Object.assign(new Error(data?.error || "לא ניתן לשמור את התהליך"), {status: response.status, code: data?.code});
                return { success: false, error: data?.error || `Failed to reorder stage templates. Status: ${response.status}` };
            }

            return { success: Boolean(data?.success) };
        } catch (error) {
            if (payload.edit_tokens != null) throw error;
            console.error("Error reordering business process stage templates:", error);
            return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
        }
    },

    reorderBusinessProcessStageTemplateLines: async (
        stageTemplateId: number | string,
        payload: ReorderBusinessProcessStageLinesPayload
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/business-process-stage-templates/${stageTemplateId}/lines/reorder`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (payload.edit_tokens != null) throw Object.assign(new Error(data?.error || "לא ניתן לשמור את התהליך"), {status: response.status, code: data?.code});
                return { success: false, error: data?.error || `Failed to reorder stage template lines. Status: ${response.status}` };
            }

            return { success: Boolean(data?.success) };
        } catch (error) {
            if (payload.edit_tokens != null) throw error;
            console.error("Error reordering business process stage template lines:", error);
            return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
        }
    },

    getCompanyProcessStages: async (companyId: number | string): Promise<CompanyProcessStage[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-process-stages/${companyId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch company process stages. Status: ${response.status}`);
            }

            const data = await response.json();
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error("Error fetching company process stages:", error);
            return [];
        }
    },

    addCompanyProcessStageFromTemplate: async (
        payload: AddCompanyProcessStageFromTemplatePayload
    ): Promise<CompanyProcessStage | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-process-stages/add-from-template`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to add company process stage from template. Status: ${response.status}. ${text}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error adding company process stage from template:", error);
            return null;
        }
    },

    updateCompanyProcessStage: async (
        id: number | string,
        payload: UpdateCompanyProcessStagePayload
    ): Promise<CompanyProcessStage | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-process-stages/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw Object.assign(new Error(`Failed to update company process stage. Status: ${response.status}. ${text}`), {status: response.status});
            }

            return await response.json();
        } catch (error) {
            if (payload.edit_token != null) throw error;
            console.error("Error updating company process stage:", error);
            return null;
        }
    },

    deleteCompanyProcessStage: async (
        id: number | string,
        options: {edit_token?: string} = {}
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-process-stages/${id}`, {
                method: "DELETE",
                ...(options.edit_token == null ? {} : {body: JSON.stringify(options)}),
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (options.edit_token != null) throw Object.assign(new Error(data?.error || "לא ניתן לשמור את התהליך"), {status: response.status, code: data?.code});
                return { success: false, error: data?.error || `Failed to delete company process stage. Status: ${response.status}` };
            }

            return { success: Boolean(data?.success) };
        } catch (error) {
            if (options.edit_token != null) throw error;
            console.error("Error deleting company process stage:", error);
            return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
        }
    },

    reorderCompanyProcessStages: async (
        companyId: number | string,
        payload: ReorderCompanyProcessStagesPayload
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-process-stages/${companyId}/reorder`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (payload.edit_tokens != null) throw Object.assign(new Error(data?.error || "לא ניתן לשמור את התהליך"), {status: response.status, code: data?.code});
                return { success: false, error: data?.error || `Failed to reorder company process stages. Status: ${response.status}` };
            }

            return { success: Boolean(data?.success) };
        } catch (error) {
            if (payload.edit_tokens != null) throw error;
            console.error("Error reordering company process stages:", error);
            return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
        }
    },

    getCompanyWidgetsBootstrap: async (companyId: number | string): Promise<CompanyWidgetsBootstrap> => {
        return dedupeInFlight(`company-widgets-bootstrap:${companyId}`, async () => {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-widgets/companies/${companyId}/bootstrap`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to fetch company widgets. Status: ${response.status}. ${text}`);
            }

            return await response.json();
        });
    },

    getCompanyCashflowAccounts: async (companyId: number | string): Promise<CashflowWidgetAccount[]> => {
        return dedupeInFlight(`cashflow-accounts:${companyId}`, async () => {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/cashflow-widget/get-accounts-for-company/${companyId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to fetch cashflow accounts. Status: ${response.status}. ${text}`);
            }

            const data = await response.json();
            return Array.isArray(data) ? data : [];
        });
    },

    getCompanyCashflowForecast: async (
        companyId: number | string,
        params?: { account_id?: string | null; range_days?: number | null; projection?: "provider" }
    ): Promise<CashflowWidgetForecast> => {
        const normalizedAccountId = params?.account_id && params.account_id !== "all"
            ? params.account_id
            : "all";
        const normalizedRangeDays = Number(params?.range_days || 0);
        const rangeDays = Number.isFinite(normalizedRangeDays) && normalizedRangeDays > 0
            ? Math.round(normalizedRangeDays)
            : null;

        return dedupeInFlight(`cashflow-forecast:${companyId}:${normalizedAccountId}:${rangeDays || "default"}:${params?.projection || "workspace"}`, async () => {
            const token = getValidToken();
            const search = new URLSearchParams();
            if (normalizedAccountId !== "all") {
                search.set("account_id", normalizedAccountId);
            }
            if (rangeDays) {
                search.set("range_days", String(rangeDays));
            }

            // The explicit Workspace endpoint owns entry inclusion. Old clients keep
            // their original endpoint, so an open pre-release tab cannot double count.
            const endpoint = shouldUseUserAccountsExperience(decodeAuthTokenClaims(token))
                ? `/api/workspace/companies/${encodeURIComponent(String(companyId))}/cashflow-forecast${params?.projection === "provider" ? "/provider" : ""}`
                : `/api/cashflow-widget/get-cashflow-widget-data/${encodeURIComponent(String(companyId))}`;
            const response = await fetch(
                `${API_URL}${endpoint}${search.toString() ? `?${search.toString()}` : ""}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                if (response.status === 409) {
                    let payload: unknown;
                    try {payload = JSON.parse(text);} catch { /* Preserve the ordinary error below. */ }
                    if (getApiErrorCode(payload) === "CASHFLOW_FORECAST_RANGE_PENDING") {
                        throw createApiRequestError({code: "CASHFLOW_FORECAST_RANGE_PENDING",
                            error: CASHFLOW_FORECAST_PENDING_MESSAGE}, 409, CASHFLOW_FORECAST_PENDING_MESSAGE);
                    }
                }
                throw new Error(`Failed to fetch cashflow widget data. Status: ${response.status}. ${text}`);
            }

            return await response.json();
        });
    },

    getCompanyRecentTransactionsWidgetData: async (
        companyId: number | string,
        params?: { account_id?: string | null; range_days?: number | null }
    ): Promise<RecentTransactionsWidgetData> => {
        const normalizedAccountId = params?.account_id && params.account_id !== "all"
            ? String(params.account_id)
            : "all";
        const normalizedRangeDays = Number(params?.range_days || 30);
        const rangeDays = [14, 30, 60, 90].includes(normalizedRangeDays)
            ? normalizedRangeDays
            : 30;

        return dedupeInFlight(`recent-transactions-widget:${companyId}:${normalizedAccountId}:${rangeDays}`, async () => {
            const token = getValidToken();
            const search = new URLSearchParams({range_days: String(rangeDays)});
            if (normalizedAccountId !== "all") {
                search.set("account_id", normalizedAccountId);
            }

            const response = await fetch(
                `${API_URL}/api/cashflow-widget/get-recent-transactions-widget-data/${companyId}?${search.toString()}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to fetch recent transactions widget data. Status: ${response.status}. ${text}`);
            }

            return await response.json();
        });
    },

    getCompanyCreditCardCharges: (companyId: number | string, params?: {months?: number}): Promise<CreditCardChargesData> =>
        fetchCreditCardCharges(companyId, params),

    getSetupCreditCardCharges: (companyId: number | string, params?: {months?: number}): Promise<CreditCardChargesData> =>
        fetchCreditCardCharges(companyId, params, true),

    getCompanyMonthlyProfitability: async (
        companyId: number | string
    ): Promise<MonthlyProfitabilityResponse> => {
        return dedupeInFlight(`monthly-profitability:${companyId}`, async () => {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-widgets/companies/${companyId}/monthly-profitability`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to fetch monthly profitability. Status: ${response.status}. ${text}`);
            }

            const data = await response.json();
            return {
                company_id: Number(data?.company_id || companyId),
                months: Array.isArray(data?.months) ? data.months : [],
                last_updated_at: typeof data?.last_updated_at === "string" ? data.last_updated_at : null,
            };
        });
    },

    getCompanyWidgetBindingSources: async (
        companyId: number | string,
        sourceType: string,
        params?: { company_month_id?: number | string | null }
    ): Promise<WidgetBindingSource[]> => {
        const token = getValidToken();
        const search = new URLSearchParams();
        if (params?.company_month_id !== undefined && params.company_month_id !== null && params.company_month_id !== "") {
            search.set("company_month_id", String(params.company_month_id));
        }

        const response = await fetch(
            `${API_URL}/api/company-widgets/companies/${companyId}/binding-sources/${sourceType}${search.toString() ? `?${search.toString()}` : ""}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }
        );

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Failed to fetch widget binding sources. Status: ${response.status}. ${text}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    },

    createCompanyWidget: async (
        companyId: number | string,
        payload: {
            widget_template_id: number;
            title?: string;
            layout_area?: string;
            order_index?: number;
            settings?: Record<string, any>;
        }
    ): Promise<CompanyWidget> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/company-widgets/companies/${companyId}/widgets`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Failed to create company widget. Status: ${response.status}. ${text}`);
        }

        return await response.json();
    },

    importCompanyWidgets: async (
        companyId: number | string,
        payload: {
            source_company_id: number;
            widget_ids?: number[];
        }
    ): Promise<number[]> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/company-widgets/companies/${companyId}/widgets/import`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Failed to import company widgets. Status: ${response.status}. ${text}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    },

    reorderCompanyWidgets: async (
        companyId: number | string,
        payload: { widgetIds: number[] }
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-widgets/companies/${companyId}/widgets/reorder`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                return { success: false, error: data?.error || `Failed to reorder company widgets. Status: ${response.status}` };
            }

            return { success: Boolean(data?.success) };
        } catch (error) {
            console.error("Error reordering company widgets:", error);
            return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
        }
    },

    updateCompanyWidget: async (
        widgetId: number | string,
        payload: {
            title?: string;
            settings?: Record<string, any>;
        }
    ): Promise<CompanyWidget> => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/company-widgets/widgets/${widgetId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Failed to update company widget. Status: ${response.status}. ${text}`);
        }

        return await response.json();
    },

    deleteCompanyWidget: async (
        widgetId: number | string
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-widgets/widgets/${widgetId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                return { success: false, error: data?.error || `Failed to delete company widget. Status: ${response.status}` };
            }

            return { success: Boolean(data?.success) };
        } catch (error) {
            console.error("Error deleting company widget:", error);
            return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
        }
    },

    getCompanyStorageFolders: async (companyId: number | string): Promise<CompanyStorageFolder[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.success || !Array.isArray(data?.data)) {
                throw new Error(data?.error || "Failed to fetch company storage folders");
            }

            return data.data;
        } catch (error) {
            console.error("Error fetching company storage folders:", error);
            return [];
        }
    },

    createCompanyStorageFolder: async (
        companyId: number | string,
        payload: { name: string; metadata?: CompanyStorageFolderMetadata | null }
    ): Promise<CompanyStorageFolder | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.success || !data?.data) {
                throw new Error(data?.error || "Failed to create company storage folder");
            }

            return data.data;
        } catch (error) {
            console.error("Error creating company storage folder:", error);
            return null;
        }
    },

    renameCompanyStorageFolder: async (
        companyId: number | string,
        folderId: string,
        payload: { name: string; metadata?: CompanyStorageFolderMetadata | null }
    ): Promise<CompanyStorageFolder | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders/${folderId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.success || !data?.data) {
                throw new Error(data?.error || "Failed to rename company storage folder");
            }

            return data.data;
        } catch (error) {
            console.error("Error renaming company storage folder:", error);
            return null;
        }
    },

    deleteCompanyStorageFolder: async (
        companyId: number | string,
        folderId: string
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders/${folderId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok || !data?.success) {
                return {
                    success: false,
                    error: data?.error || "Failed to delete company storage folder"
                };
            }

            return data?.data || { success: true };
        } catch (error) {
            console.error("Error deleting company storage folder:", error);
            return {
                success: false,
                error: error instanceof Error ? error.message : "Unknown error"
            };
        }
    },

    getCompanyStorageFiles: async (
        companyId: number | string,
        folderId: string
    ): Promise<CompanyStorageFile[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders/${folderId}/files`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.success || !Array.isArray(data?.data)) {
                throw new Error(data?.error || "Failed to fetch company storage files");
            }

            return data.data;
        } catch (error) {
            console.error("Error fetching company storage files:", error);
            throw (error instanceof Error ? error : new Error("Unknown error"));
        }
    },

    createCompanyStorageUploadTargets: async (
        companyId: number | string,
        folderId: string,
        payload: {
            files: Array<{
                name: string;
                contentType?: string;
                size?: number;
            }>;
            expiresInSeconds?: number;
        }
    ): Promise<CompanyStorageUploadTarget[]> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders/${folderId}/uploads/signed-urls`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.success || !Array.isArray(data?.data)) {
                throw new Error(data?.error || "Failed to create company storage upload targets");
            }

            return data.data;
        } catch (error) {
            console.error("Error creating company storage upload targets:", error);
            return [];
        }
    },

    finalizeCompanyStorageUploads: async (
        companyId: number | string,
        folderId: string,
        payload: { fileIds: string[] }
    ): Promise<FinalizeCompanyStorageUploadsResponse> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders/${folderId}/uploads/complete`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.success || !data?.data) {
                throw new Error(data?.error || "Failed to finalize company storage uploads");
            }

            return {
                completed: Array.isArray(data.data.completed) ? data.data.completed : [],
                failed: Array.isArray(data.data.failed) ? data.data.failed : [],
            };
        } catch (error) {
            console.error("Error finalizing company storage uploads:", error);
            return {
                completed: [],
                failed: []
            };
        }
    },

    renameCompanyStorageFile: async (
        companyId: number | string,
        folderId: string,
        fileId: string,
        payload: { name: string }
    ): Promise<CompanyStorageFile | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders/${folderId}/files/${fileId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.success || !data?.data) {
                throw new Error(data?.error || "Failed to rename company storage file");
            }

            return data.data;
        } catch (error) {
            console.error("Error renaming company storage file:", error);
            return null;
        }
    },

    deleteCompanyStorageFile: async (
        companyId: number | string,
        folderId: string,
        fileId: string
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders/${folderId}/files/${fileId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok || !data?.success) {
                return {
                    success: false,
                    error: data?.error || "Failed to delete company storage file"
                };
            }

            return data?.data || { success: true };
        } catch (error) {
            console.error("Error deleting company storage file:", error);
            return {
                success: false,
                error: error instanceof Error ? error.message : "Unknown error"
            };
        }
    },

    getCompanyStorageDownloadUrl: async (
        companyId: number | string,
        folderId: string,
        fileId: string
    ): Promise<{
        fileId: string;
        fileName: string;
        contentType: string;
        downloadUrl: string;
    } | null> => {
        try {
            const token = getValidToken();
            const response = await fetch(`${API_URL}/api/company-storage/${companyId}/folders/${folderId}/files/${fileId}/download-url`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.success || !data?.data) {
                throw new Error(data?.error || "Failed to get company storage download url");
            }

            return data.data;
        } catch (error) {
            console.error("Error getting company storage download url:", error);
            return null;
        }
    }
    ,

    createBusiness: async (payload: {
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        organizationNumber?: string | null;
        businessTypeId: number | string;
    }): Promise<
        | { success: true; data: { business: any; company: any; user: any } }
        | { success: false; message?: string }
    > => {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/business/create-business`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
            const message = getApiErrorMessage(
                data,
                `Request failed (${response.status})`
            );
            return { success: false, message };
        }

        if (!data || typeof data !== "object") {
            return { success: false, message: "Invalid server response" };
        }

        if (data.success === true) {
            return data as { success: true; data: { business: any; company: any; user: any } };
        }

        return {
            success: false,
            message: getApiErrorMessage(data, "Failed to create business"),
        };
    },
    };
};

export const api = createApiClient("legacy");
export type ApiClient = typeof api;

// Named exports to maintain existing import style across the codebase
export const {
    getBusinesses,
    getAllBusinesses,
    deleteBusiness,
    getUserMessageTypes,
    setUserMessageTypes,
    getBusinessGuidelines,
    handleBudgetLogic,
    fetchMessagesByCompanyId,
    fetchNewCompanies,
    getCompanyStatuses,
    syncCompanyContacts,
    addUser,
    editUser,
    deleteUser,
    getUsersByBusinessId,
    getCurrentUserOperators,
    getUsersByCompany,
    getManageMessagesUsersList,
    getManageMessagesList,
    getManagedAssistants,
    getAssistantDetails,
    updateAssistant,
    deleteMessage,
    sendMessageToCompany,
    executeRun,
    updateCompany,
    updateCashFlowDetails,
    updateTotals,
    getActiveCompanies,
    getCurrentUserCompanies,
    getCurrentUserBusinesses,
    getCurrentUserAccessProfile,
    getServerIp,
    setEventStatus,
    getMessageById,
    getEvents,
    getLatestThinkProcess,
    getFullThinkProcess,
    getTranscription,
    getCompanyMeetings,
    getAllMeetingsByIds,
    updateCompanyStatus,
    getThinkProcess,
    endMeeting,
    updateMeetingSummary,
    getMeetingSummary,
    getHistoryThinkProcess,
    getTasks,
    generateTasks,
    updateTask,
    approveTask,
    getFeedback,
    generateFeedback,
    createCompanyWithUser,
    getCompanyDetails,
    getEditJampaCompanyDetails,
    updateEditJampaCompany,
    updateCompanyDetails,
    getFinanceData,
    editFinanceData,
    createTaskEvent,
    updateTaskEvent,
    deleteJampaStrengtheningEvent,
    updateJampaStrengtheningEvent,
    replaceJampaStrengtheningEvent,
    refreshSummary,
    getWorkspaceBudgetReport,
    downloadWorkspaceBudgetReport,
    createBudgetReport,
    createBudgetReportExcel,
    getCalendarWeek,
    setBusinessGuidelinesSender,
    getMonthlyCashflowData,
    getFutureMonthlyCashflowTargets,
    getFutureCashflowGapClassificationTypes,
    updateFutureCashflowGapClassification,
    clearFutureCashflowGapClassification,
    updateFutureMonthlyCashflowTarget,
    lockFutureMonthlyCashflowTargets,
    updateFutureCashflowStartBalance,
    updateFutureCashflowExpenseClassification,
    getFutureCashflowExpenseClassificationRecommendations,
    downloadFutureCashflowReportExcel,
    getMultiCompanyMonthlyCashflowData,
    getMultiCompanyMonthlyCashflowDataExcel,
    getMonthlyCashflowDataExcel,
    sendPdfToEmail,
    showCategorySuggestion,
    showHistoryTransactions,
    approveCategorySuggestion,
    showAllowedCategories,
    changeCategorySuggestion,
    memory,
    deleteMeetingSummary,
    deleteTopicSummaryRow,
    getMonthlyCashflowReportUsers,
    getMonthlyCashflowAccounts,
    createActionItem,
    meetingPreparation,
    startRecording,
    approveMeeting,
    getMeetingSummaryUsers,
    sendMeetingSummary,
    sendWhatsappFreeTextMessageToUser,
    downloadWhatsappFile,
    getWhatsappFileBlobUrl,
    getUserMessageTypesByCompanyId,
    getCategoriesWithDiff,
    getBudgetSettings,
    setBudgetSettings,
    refreshBudgetReport,
    updateCashFlowDetailsByCompanyId,
    snoozeBudgetCategory,
    getMonthlyTargets,
    updateWorkingDays,
    createTarget,
    updateTarget,
    deleteTarget,
    deleteTargetDefinition,
    uploadMeetingAudioViaSignedUrl,
    getAgentLogs,
    getAgentChatHistory,
    analyzeAgentLogs,
    getThinkingProcessByUserQuestionId,
    uploadMeetingTranscript,
    getSynthesizedMessages,
    getBusinessProcessPrograms,
    getBusinessProcessProgramsForSettings,
    createBusinessProcessProgram,
    updateBusinessProcessProgram,
    deleteBusinessProcessProgram,
    getBusinessProcessStageTemplates,
    createBusinessProcessStageTemplate,
    updateBusinessProcessStageTemplate,
    deleteBusinessProcessStageTemplate,
    addBusinessProcessStageTemplateLine,
    updateBusinessProcessStageTemplateLine,
    deleteBusinessProcessStageTemplateLine,
    reorderBusinessProcessStageTemplates,
    reorderBusinessProcessStageTemplateLines,
    getCompanyProcessStages,
    addCompanyProcessStageFromTemplate,
    updateCompanyProcessStage,
    deleteCompanyProcessStage,
    reorderCompanyProcessStages,
    getCompanyWidgetsBootstrap,
    getCompanyCashflowAccounts,
    getCompanyCashflowForecast,
    getCompanyRecentTransactionsWidgetData,
    getCompanyCreditCardCharges,
    getCompanyMonthlyProfitability,
    getCompanyWidgetBindingSources,
    createCompanyWidget,
    importCompanyWidgets,
    reorderCompanyWidgets,
    updateCompanyWidget,
    deleteCompanyWidget,
    getCompanyStorageFolders,
    createCompanyStorageFolder,
    renameCompanyStorageFolder,
    deleteCompanyStorageFolder,
    getCompanyStorageFiles,
    createCompanyStorageUploadTargets,
    finalizeCompanyStorageUploads,
    renameCompanyStorageFile,
    deleteCompanyStorageFile,
    getCompanyStorageDownloadUrl,
    createBusiness,
    getBillingCollectionsDashboard,
    getBillingRecurringSales,
    refreshBillingRecurringSales,
    getBillingSaleHistory,
    createBillingReceipt,
    addBillingAccount,
    saveBillingAccountCard,
    assignBillingCompanyAccount,
    chargeBillingImmediateAccount,
    addBillingIgnoredAccount,
    removeBillingIgnoredAccount,
    addBillingPaymentPage,
} = api;
